import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from 'sonner';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, ExternalLink, Sparkles, Trash2, Upload, FileText, Image as ImageIcon, MoreHorizontal, FilterX, X as XIcon, Bell, BellOff, LayoutGrid, Table as TableIcon, ClipboardList, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Hash, Users, Search as SearchIcon, Pause, Play, XCircle, Loader2, AlertTriangle, Cpu, Paperclip, CalendarRange, CheckCircle2, Palette, Pencil, CalendarClock, History, ArrowRight } from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { formatDate, formatDateTime, formatDaysSince } from '@/lib/format';
import { extractError } from '@/lib/errors';
import { PurityFormDialog } from '@/components/phase10/PurityFormDialog';
import { PdfDownloadDialog } from '@/components/phase11/PdfDownloadDialog';
import { GoldsmithFormDialog } from '@/components/phase11/GoldsmithFormDialog';
import { CustomerCombobox } from '@/components/customers/CustomerCombobox';
import { StageNotifyPicker } from '@/components/production/StageNotifyPicker';
import { EditOrderSlaDialog } from '@/components/production/EditOrderSlaDialog';
import { SavedViewsMenu } from '@/components/orders/SavedViewsMenu';
import { ReferenceFilter, ReferenceFilterChips } from '@/components/orders/ReferenceFilter';
import { ReferenceCombobox } from '@/components/orders/ReferenceCombobox';
import { useReferenceConfig } from '@/hooks/useReferenceConfig';
import { useDesignConfig } from '@/hooks/useDesignConfig';
import { DesignPicker } from '@/components/designs/DesignPicker';
import { DesignNoBadge } from '@/components/designs/DesignNoBadge';
import { ReferenceChip } from '@/components/ReferenceChip';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const STAGE_COLORS = {
  'Issued': { bg: 'var(--diamond-bg)', text: 'var(--diamond-icon)' },
  'In Production': { bg: 'var(--gold-bg)', text: 'var(--gold-icon)' },
  'Quality Check': { bg: 'var(--jewellery-bg)', text: 'var(--jewellery-icon)' },
  'Received Back': { bg: 'var(--goldsmith-bg)', text: 'var(--goldsmith-icon)' },
  'Completed': { bg: '#EAF7F5', text: '#0F766E' },
  // Wave 3.71 — Cancelled orders (post karigar-shift) use a subdued slate
  // scheme so operators can see at a glance the row is archival, not live.
  'Cancelled': { bg: '#E2E8F0', text: '#475569' },
};

export function StageBadge({ stage, substage, holdStatus, activeSteps }) {
  if (holdStatus === 'on_hold') {
    return (
      <div className="inline-flex flex-wrap items-center gap-1">
        <Badge style={{ background: '#FEF3C7', color: '#92400E', border: 'none' }} className="text-[10px]">On Hold</Badge>
        <span className="text-[10px] text-slate-500">· {stage}</span>
      </div>
    );
  }
  // Wave 3.89 — When the order has parallel active steps on the
  // Production Checklist, render a stacked pill per active step so the
  // operator sees at a glance every workstream currently in play (e.g.
  // "CAD Confirmed" + "Diamonds Issued"). Falls back to the legacy
  // top-level `stage` pill when no active steps are supplied.
  const isTerminal = stage === 'Completed' || stage === 'Cancelled';
  const hasActive = Array.isArray(activeSteps) && activeSteps.length > 0;
  if (isTerminal || !hasActive) {
    const c = STAGE_COLORS[stage] || STAGE_COLORS['Issued'];
    return (
      <div className="inline-flex flex-wrap items-center gap-1">
        <Badge style={{ background: c.bg, color: c.text, border: 'none' }} className="text-[10px]">{stage}</Badge>
        {substage && <span className="text-[10px] text-slate-500">· {substage}</span>}
      </div>
    );
  }
  return (
    <div className="inline-flex flex-wrap items-center gap-1" data-testid="orders-stage-multi-pills">
      {activeSteps.map((s) => {
        // Wave 4.3y — Ghat now carries a refined 4-state gold buyer status so
        // the Stage column reads the exact pipeline position instead of a
        // generic "Waiting for gold". Amber = still blocked on gold; blue =
        // active (gold issued / ready to confirm or any other active step).
        const isGhat = (s.step || '').toLowerCase() === 'ghat';
        const gs = isGhat ? (s.gold_status || '') : '';
        let waiting = s.sla_status === 'waiting';
        let text = s.label;
        let title = `${s.label} is currently active on the Production Checklist`;
        if (gs === 'pending_gold_order') {
          waiting = true; text = `${s.label} · Pending Gold Order`;
          title = `${s.label}: gold requirement raised, gold order not placed yet`;
        } else if (gs === 'waiting_for_gold') {
          waiting = true; text = `${s.label} · Waiting for Gold`;
          title = `${s.label}: gold order raised, waiting for gold to be issued`;
        } else if (gs === 'no_requirement') {
          waiting = true; text = `${s.label} · No Requirement`;
          title = `${s.label}: no gold requirement raised yet`;
        } else if (gs === 'ghat_confirmation') {
          waiting = false; text = s.label;
          title = `${s.label}: gold issued — ready to confirm`;
        } else if (waiting) {
          text = `${s.label} · Waiting for gold`;
          title = `${s.label} is waiting for gold to be issued`;
        }
        return (
          <Badge
            key={s.step}
            className={`text-[10px] font-medium border ${
              waiting
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : 'bg-blue-50 text-blue-700 border-blue-200'
            }`}
            data-testid={`orders-stage-pill-${s.step}`}
            title={title}
          >
            {text}
          </Badge>
        );
      })}
    </div>
  );
}

// Wave 4.3y — Ghat stage split matcher for object-shaped ``active_steps``
// (each entry = { step, label, sla_status, gold_status }). Ghat uses a refined
// 4-state gold buyer status so the Stage filter can distinguish:
//   'ghat_pending_gold_order' → gold requirement raised, order not raised yet
//   'ghat_waiting'            → gold order raised, waiting for gold to be issued
//   'ghat'                    → active WITH gold issued (ready to confirm)
// Falls back to the legacy sla_status when gold_status is absent.
export function activeStepsMatch(steps, key) {
  const k = (key || '').toLowerCase();
  const arr = Array.isArray(steps) ? steps : [];
  if (k === 'ghat' || k === 'ghat_waiting' || k === 'ghat_pending_gold_order') {
    return arr.some((s) => {
      if ((s?.step || '').toLowerCase() !== 'ghat') return false;
      const gs = (s?.gold_status || '').toLowerCase();
      const waiting = s?.sla_status === 'waiting';
      if (k === 'ghat_pending_gold_order') return gs === 'pending_gold_order';
      if (k === 'ghat_waiting') return gs === 'waiting_for_gold' || (!gs && waiting);
      return gs === 'ghat_confirmation' || (!gs && !waiting);
    });
  }
  return arr.some((s) => (s?.step || '').toLowerCase() === k);
}

// Wave 4.3y — Contribute the correct Ghat variant key(s) an order's active
// steps satisfy, so cascade option-sets only surface reachable values.
export function activeStepKeys(steps) {
  const out = [];
  for (const s of (Array.isArray(steps) ? steps : [])) {
    const step = (s?.step || '').toLowerCase();
    if (!step) continue;
    if (step === 'ghat') {
      const gs = (s?.gold_status || '').toLowerCase();
      const waiting = s?.sla_status === 'waiting';
      if (gs === 'pending_gold_order') out.push('ghat_pending_gold_order');
      else if (gs === 'waiting_for_gold' || (!gs && waiting)) out.push('ghat_waiting');
      else out.push('ghat');
    } else {
      out.push(s.step);
    }
  }
  return out;
}

// Wave 4.10a — In-House CAD development sub-stage of an order (mirrors the
// backend ``cad_substage`` + the Production Digest "Internal CAD" group).
// Returns 'not_allotted' | 'wip' while the order is still in the
// internal CAD development window, or null for external-CAD orders and orders
// whose CAD step is done (Final → moved on to Ghat).
export function cadInternalSubstage(o) {
  if (!o || o.in_house_cad === false) return null;
  const cadDone = !!o.cad_done;
  // Wave 4.10d — Once CAD itself is done (Final) the order has LEFT the Internal
  // CAD window: the Final flow pushes it to Ghat "Waiting for Gold", so Internal
  // CAD only ever reports two live sub-stages — Not Allotted and WIP.
  if (cadDone || o.cad_final_done) return null;
  if (o.cad_wip_done || o.cad_allot_done || (o.cad_developer_id || '')) return 'wip';
  return 'not_allotted';
}

// Wave 4.x — Human labels for step-key Stage filter values (used by the
// filter chip so a raw key like ``ghat_waiting`` reads nicely).
export const STAGE_FILTER_LABELS = {
  cad: 'CAD Active',
  cad_not_allotted: 'CAD · Not Allotted',
  cad_wip: 'CAD · WIP',
  cam: 'CAM Active',
  chamka: 'Chamka Active',
  ghat_pending_gold_order: 'Ghat - Pending Gold Order',
  ghat_waiting: 'Ghat - Waiting For Gold',
  ghat: 'Ghat Active',
  raw_material: 'Raw Material Active',
  setting: 'Setting Active',
  qc: 'Quality Check Active',
  delivery: 'Delivery Active',
};

// Wave 3.73 — Filter chip row on the Orders list.
// ---------------------------------------------------------------------------
// Each active (non-default) filter is rendered as a coloured pill with an
// inline ✕ so the operator can drop that one filter without touching the
// others. This is faster than scanning the six dropdowns above and it
// answers the "what am I currently filtered by?" question at a glance.
//
// Default values considered "no filter":
//   status:      'active'
//   goldsmith_id '' (empty)
//   customer_id  '' (empty)
//   stage:       'all'
//   substage:    'all'
// ---------------------------------------------------------------------------
const STATUS_CHIP_LABELS = {
  active: 'Active',
  on_hold: 'On Hold',
  completed: 'Completed',
  cancelled: 'Cancelled',
  open: 'All Open',
  all: 'All',
};

// Tailwind palette per filter category. Kept intentionally muted so the
// chips don't compete with the table below.
const CHIP_TONES = {
  status:    'bg-blue-50 text-blue-700 border-blue-200',
  goldsmith: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  customer:  'bg-violet-50 text-violet-700 border-violet-200',
  stage:     'bg-amber-50 text-amber-700 border-amber-200',
  substage:  'bg-rose-50 text-rose-700 border-rose-200',
  pending:   'bg-teal-50 text-teal-700 border-teal-200',
  material:  'bg-sky-50 text-sky-700 border-sky-200',
  attachments: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

// Wave 3.99u — Raw-material metadata used by both the Pending filter
// popover and the chip row. `key` matches the material slug returned
// by /api/dashboard/pending-requirements ('diamond' / 'color_stone' /
// 'pearl' / 'gold').
const RAW_MATERIALS = [
  { key: 'gold',        label: 'Gold' },
  { key: 'diamond',     label: 'Diamonds' },
  { key: 'color_stone', label: 'Colour Stones' },
  { key: 'pearl',       label: 'Pearls' },
];
const RAW_MATERIAL_LABEL = Object.fromEntries(RAW_MATERIALS.map((m) => [m.key, m.label]));

// Wave 4.10b — Raw Material buyer-tracking (Order Pending / Order Raised /
// Material Collected) filter helpers. Side materials only (gold has no buyer
// tracking). Mirrors the backend ``rm_buyer_status`` classifier so the Orders
// list + Dashboard can filter by procurement state.
const RM_SIDE_MATERIALS = [
  { key: 'diamond', label: 'Diamonds' },
  { key: 'color_stone', label: 'Colour Stones' },
  { key: 'pearl', label: 'Pearls' },
];
const RM_STATUS_ORDER = ['order_pending', 'order_raised', 'material_collected'];
const RM_STATUS_LABELS = {
  order_pending: 'Order Pending',
  order_raised: 'Order Raised',
  material_collected: 'Material Collected',
};

function rmReqState(o, kind) {
  let has = false;
  let pending = false;
  for (const r of (o.requirements || [])) {
    if ((r.material_type || '').toLowerCase() !== kind) continue;
    has = true;
    if (r.manually_completed) continue;
    const st = (r.status || 'pending').toLowerCase();
    if (!['complete', 'completed', 'done'].includes(st)) pending = true;
  }
  return [has, pending];
}

// Returns 'order_pending' | 'order_raised' | 'material_collected' | 'issued'
// | null (no requirement for that side material).
export function rmBuyerStatusOf(o, kind) {
  const [has, pending] = rmReqState(o, kind);
  if (!has) return null;
  if (!pending) return 'issued';
  if (o[`rm_${kind}_material_collected_done`]) return 'material_collected';
  if (o[`rm_${kind}_order_raised_done`]) return 'order_raised';
  return 'order_pending';
}

function FilterChip({ tone, label, value, onRemove, testId }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${tone}`}
      data-testid={testId}
    >
      <span className="text-[10px] uppercase tracking-wide opacity-70">{label}</span>
      <span className="max-w-[160px] truncate">{value}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 inline-flex items-center rounded-full hover:bg-black/10 p-0.5 transition-colors"
        aria-label={`Remove ${label} filter`}
        data-testid={`${testId}-remove`}
      >
        <XIcon size={11} />
      </button>
    </span>
  );
}

export function FilterChipRow({ filter, onChange, goldsmiths, customers, referenceLabel = 'Page No.' }) {
  const chips = [];
  const refLabelShort = (referenceLabel.split(' ')[0] || referenceLabel).replace(/\.$/, '');

  if (filter.status && filter.status !== 'active') {
    chips.push({
      key: 'status',
      tone: CHIP_TONES.status,
      label: 'Status',
      value: STATUS_CHIP_LABELS[filter.status] || filter.status,
      testId: 'orders-filter-chip-status',
      onRemove: () => onChange({ ...filter, status: 'active' }),
    });
  }

  if (filter.goldsmith_id) {
    const g = (goldsmiths || []).find((x) => x.id === filter.goldsmith_id);
    chips.push({
      key: 'goldsmith',
      tone: CHIP_TONES.goldsmith,
      label: 'Goldsmith',
      value: g?.name || 'Selected',
      testId: 'orders-filter-chip-goldsmith',
      onRemove: () => onChange({ ...filter, goldsmith_id: '' }),
    });
  }

  if (filter.customer_id) {
    const c = (customers || []).find((x) => x.id === filter.customer_id);
    chips.push({
      key: 'customer',
      tone: CHIP_TONES.customer,
      label: 'Customer',
      value: c?.name || 'Selected',
      testId: 'orders-filter-chip-customer',
      onRemove: () => onChange({ ...filter, customer_id: '' }),
    });
  }

  if (filter.stage && filter.stage !== 'all') {
    chips.push({
      key: 'stage',
      tone: CHIP_TONES.stage,
      label: 'Stage',
      value: STAGE_FILTER_LABELS[filter.stage] || filter.stage,
      testId: 'orders-filter-chip-stage',
      // Dropping the stage also clears any substage filter, since
      // substage is only meaningful during In Production.
      onRemove: () => onChange({ ...filter, stage: 'all', substage: 'all' }),
    });
  }

  if (filter.substage && filter.substage !== 'all') {
    chips.push({
      key: 'substage',
      tone: CHIP_TONES.substage,
      label: 'Substage',
      value: filter.substage === '__none__' ? 'None' : filter.substage,
      testId: 'orders-filter-chip-substage',
      onRemove: () => onChange({ ...filter, substage: 'all' }),
    });
  }

  // Wave 4.x — CAD source (In-House vs External) filter chip.
  if (filter.cad_source && filter.cad_source !== 'all') {
    chips.push({
      key: 'cad_source',
      tone: CHIP_TONES.stage,
      label: 'CAD',
      value: filter.cad_source === 'external' ? 'External' : 'In-House',
      testId: 'orders-filter-chip-cad-source',
      onRemove: () => onChange({ ...filter, cad_source: 'all' }),
    });
  }

  // Wave 4.10c — Jewellery Type filter chip (replaced the old Files chip).
  if (filter.jewellery_type) {
    chips.push({
      key: 'jewellery_type',
      tone: CHIP_TONES.attachments,
      label: 'Type',
      value: filter.jewellery_type,
      testId: 'orders-filter-chip-jewellery-type',
      onRemove: () => onChange({ ...filter, jewellery_type: '' }),
    });
  }

  // Wave 3.99u — Pending-requirements filter chips. The pending chip
  // appears alone when the operator toggled "Only orders with pending
  // requirements" without narrowing further. Each material selection
  // then gets its own chip so buyers can drop them one at a time.
  if (filter.pending_only) {
    chips.push({
      key: 'pending',
      tone: CHIP_TONES.pending,
      label: 'Pending',
      value: 'Only pending',
      testId: 'orders-filter-chip-pending',
      // Dropping the Pending master toggle also clears the material
      // sub-filter so we never leave a dangling "Material: Diamonds"
      // filter that has no toggle context.
      onRemove: () => onChange({ ...filter, pending_only: false, pending_materials: [] }),
    });
  }
  if (Array.isArray(filter.pending_materials) && filter.pending_materials.length) {
    filter.pending_materials.forEach((m) => {
      chips.push({
        key: `pending-material-${m}`,
        tone: CHIP_TONES.material,
        label: 'Material',
        value: RAW_MATERIAL_LABEL[m] || m,
        testId: `orders-filter-chip-pending-material-${m}`,
        onRemove: () => onChange({
          ...filter,
          pending_materials: (filter.pending_materials || []).filter((x) => x !== m),
        }),
      });
    });
  }

  // Wave 4.10e — Raw Material buyer-status chip (Order Pending / Order Raised /
  // Material Collected). Scoped to pending side-material requirements.
  if (filter.rm_status && filter.rm_status !== 'all') {
    chips.push({
      key: 'rm-status',
      tone: CHIP_TONES.material,
      label: 'RM Status',
      value: RM_STATUS_LABELS[filter.rm_status] || filter.rm_status,
      testId: 'orders-filter-chip-rm-status',
      onRemove: () => onChange({ ...filter, rm_status: 'all' }),
    });
  }

  // Reference-Number system — one removable chip per selected reference.
  if (Array.isArray(filter.reference_nos) && filter.reference_nos.length) {
    filter.reference_nos.forEach((ref) => {
      chips.push({
        key: `reference-${ref}`,
        tone: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        label: refLabelShort,
        value: String(ref),
        testId: `orders-filter-chip-reference-${ref}`,
        onRemove: () => onChange({
          ...filter,
          reference_nos: (filter.reference_nos || []).filter((x) => x !== ref),
        }),
      });
    });
  }

  if (chips.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="orders-filter-chip-row"
    >
      <span className="text-xs font-medium text-slate-500 pl-0.5">
        Filters applied
      </span>
      {chips.map((chip) => (
        <FilterChip
          key={chip.key}
          tone={chip.tone}
          label={chip.label}
          value={chip.value}
          onRemove={chip.onRemove}
          testId={chip.testId}
        />
      ))}
    </div>
  );
}

export default function Orders() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell title="Order Forms" subtitle="Track manufacturing work issued to goldsmiths.">
      <CompanyGate>
        <OrdersContent companyId={effectiveCompanyId} />
      </CompanyGate>
      <ScrollToTopButton />
    </AppShell>
  );
}

// Wave 4.x — Reusable, colour-accented pagination bar for the Orders list.
// Rendered at BOTH the top and bottom of the list so operators never have to
// scroll to change page. ``position`` keeps every data-testid unique.
function OrdersPaginationBar({ position, total, rangeStart, rangeEnd, pageSize, setPageSize, pageSizeOptions, page, totalPages, goToPage, pageJump, setPageJump, totalKnown = true, loadingMore = false }) {
  if (!total && !loadingMore) return null;
  const pos = position || 'bottom';
  const totalTxt = totalKnown ? String(total) : '…';
  const totalPagesTxt = totalKnown ? String(totalPages) : '…';
  return (
    <div
      className="mb-3 flex flex-col gap-3 rounded-lg border border-[var(--jewellery-icon)]/30 bg-[var(--jewellery-bg)] px-3 py-2 shadow-sm sm:flex-row sm:items-center sm:justify-between"
      data-testid={`orders-pagination-${pos}`}
    >
      <div className="flex items-center gap-3 text-xs text-[var(--ink-navy)]">
        <span data-testid={`orders-pagination-range-${pos}`}>
          Showing <span className="font-mono tabular-nums font-semibold">{rangeStart}–{rangeEnd}</span> of{' '}
          <span className="font-mono tabular-nums font-semibold">{totalTxt}</span>
        </span>
        {loadingMore && (
          <span className="inline-flex items-center gap-1 text-[11px] text-[var(--jewellery-icon)]" data-testid={`orders-loading-more-${pos}`}>
            <Loader2 size={12} className="animate-spin" /> loading all…
          </span>
        )}
        <div className="flex items-center gap-1.5">
          <span>Per page</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="h-8 w-[74px] bg-white" data-testid={`orders-page-size-${pos}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((n) => (
                <SelectItem key={n} value={String(n)} data-testid={`orders-page-size-${pos}-${n}`}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-white" onClick={() => goToPage(1)} disabled={page <= 1} data-testid={`orders-page-first-${pos}`} aria-label="First page"><ChevronsLeft size={15} /></Button>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-white" onClick={() => goToPage(page - 1)} disabled={page <= 1} data-testid={`orders-page-prev-${pos}`} aria-label="Previous page"><ChevronLeft size={15} /></Button>
        <span className="px-1 text-xs font-medium text-[var(--ink-navy)] whitespace-nowrap" data-testid={`orders-page-indicator-${pos}`}>
          Page <span className="font-mono tabular-nums">{page}</span> / <span className="font-mono tabular-nums">{totalPagesTxt}</span>
        </span>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-white" onClick={() => goToPage(page + 1)} disabled={totalKnown && page >= totalPages} data-testid={`orders-page-next-${pos}`} aria-label="Next page"><ChevronRight size={15} /></Button>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-white" onClick={() => goToPage(totalPages)} disabled={!totalKnown || page >= totalPages} data-testid={`orders-page-last-${pos}`} aria-label="Last page"><ChevronsRight size={15} /></Button>
        <form onSubmit={(e) => { e.preventDefault(); goToPage(pageJump); }} className="ml-1 flex items-center gap-1.5">
          <Input type="number" min={1} max={totalKnown ? totalPages : undefined} value={pageJump} onChange={(e) => setPageJump(e.target.value)} placeholder="Go to" className="h-8 w-[70px] bg-white" data-testid={`orders-page-jump-input-${pos}`} aria-label="Go to page number" />
          <Button type="submit" variant="outline" size="sm" className="h-8 bg-white" data-testid={`orders-page-jump-go-${pos}`} disabled={!pageJump}>Go</Button>
        </form>
      </div>
    </div>
  );
}

function OrdersContent({ companyId }) {
  const navigate = useNavigate();
  const { isSuperAdmin, isCompanyAdmin, isSalesRole, hasPermission } = useAuth();
  const canToggleSla = !!(isSuperAdmin || isCompanyAdmin);
  // Wave 4.x — Standard order-edit right. Gates the inline Estimated
  // Completion editor on Grid cards. Mirrors the broad "Edit Order Forms"
  // permission (admins + order managers always qualify).
  const canEditOrders = !!(isSuperAdmin || isCompanyAdmin || hasPermission('orders.edit') || hasPermission('orders.edit_header'));
  // Wave 4.x — Bulk order-form actions (Company Admin / Super Admin only):
  // multi-select rows/cards then Hold / Resume / Cancel / Delete with a single
  // batch confirmation. Selection state + helpers live here so both the Grid
  // and Table renderers can share them.
  const canBulk = !!(isSuperAdmin || isCompanyAdmin);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'hold'|'resume'|'cancel'|'delete'
  const [bulkReason, setBulkReason] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // Bulk "Assign Design" — tag many selected orders to one existing design.
  const [bulkDesignOpen, setBulkDesignOpen] = useState(false);
  const [bulkDesignId, setBulkDesignId] = useState(null);
  const [bulkDesignBusy, setBulkDesignBusy] = useState(false);
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const BULK_META = {
    hold:   { title: 'Put orders on hold', verb: 'put on hold', endpoint: '/orders/bulk/hold', needsReason: true, danger: false },
    resume: { title: 'Resume orders',      verb: 'resumed',     endpoint: '/orders/bulk/resume', needsReason: false, danger: false },
    cancel: { title: 'Cancel orders',      verb: 'cancelled',   endpoint: '/orders/bulk/cancel', needsReason: false, danger: true },
    delete: { title: 'Delete orders',      verb: 'deleted',     endpoint: '/orders/bulk/delete', needsReason: false, danger: true },
  };
  const openBulk = (action) => { setBulkReason(''); setBulkAction(action); };
  const runBulk = async () => {
    if (!bulkAction) return;
    const meta = BULK_META[bulkAction];
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { setBulkAction(null); return; }
    setBulkBusy(true);
    try {
      const body = { order_ids: ids };
      if (meta.needsReason) body.reason = bulkReason.trim() || undefined;
      const { data } = await api.post(meta.endpoint, body);
      const ok = data?.success_count ?? 0;
      const fail = data?.fail_count ?? 0;
      const failedNames = (data?.failed || []).map((f) => f.order_no || f.order_id).slice(0, 3).join(', ');
      if (ok > 0 && fail === 0) {
        toast.success(`${ok} order${ok === 1 ? '' : 's'} ${meta.verb}.`);
      } else if (ok > 0 && fail > 0) {
        toast.warning(`${ok} ${meta.verb}, ${fail} skipped${failedNames ? `: ${failedNames}${fail > 3 ? '…' : ''}` : ''}.`);
      } else {
        toast.error(`No orders ${meta.verb}${(data?.failed || [])[0]?.reason ? ` — ${data.failed[0].reason}` : ''}.`);
      }
      clearSelection();
      setBulkAction(null);
      await load();
      await loadMaster();
      try { await loadPending(); } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(extractError(e, `Bulk ${bulkAction} failed`));
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkAssignDesign = async () => {
    const ids = Array.from(selectedIds);
    if (!bulkDesignId || ids.length === 0) return;
    setBulkDesignBusy(true);
    try {
      const { data } = await api.post('/designs/assign-bulk', { order_ids: ids, design_id: bulkDesignId });
      const ok = data?.success_count ?? 0;
      const fail = data?.fail_count ?? 0;
      if (ok > 0 && fail === 0) toast.success(`${ok} order${ok === 1 ? '' : 's'} linked to ${data.design_no}.`);
      else if (ok > 0 && fail > 0) toast.warning(`${ok} linked to ${data.design_no}, ${fail} skipped.`);
      else toast.error('No orders were linked.');
      setBulkDesignOpen(false);
      setBulkDesignId(null);
      clearSelection();
      await load();
      await loadMaster();
    } catch (e) {
      toast.error(extractError(e, 'Bulk assign failed'));
    } finally {
      setBulkDesignBusy(false);
    }
  };
  // Reference-Number system — enabled per company (Super Admin gated).
  const { enabled: refEnabled, nextPreview: refNextPreview, label: refLabel, labelShort: refLabelShort, refresh: refreshRefConfig } = useReferenceConfig(companyId);
  // Design Master — enabled per company (Super Admin gated). When ON a design
  // is mandatory to create a new order.
  const { enabled: designEnabled, nextPreview: designNextPreview, refresh: refreshDesignConfig } = useDesignConfig(companyId);
  // Wave 3.99ac — Deep-link support. Other pages (e.g. the Customers
  // master "N open" chip) can hand the Orders list a pre-set filter via
  // URL query params. We read these ONCE on mount to seed the initial
  // filter state, then strip them from the URL so they don't linger or
  // re-apply after the operator adjusts filters.
  const [searchParams, setSearchParams] = useSearchParams();
  const ALLOWED_STATUS = React.useMemo(
    () => new Set(['active', 'on_hold', 'completed', 'cancelled', 'open', 'all']),
    [],
  );
  const [list, setList] = useState([]);
  // Wave 4.x — Progressive loading state. ``ordersFullyLoaded`` flips false at
  // the start of every fetch and true once the COMPLETE set has arrived.
  // ``ordersServerCount`` is a cheap server-side total used to fill the
  // pagination bar instantly while the full list streams in behind the fast
  // first page. ``loadSeq`` guards against out-of-order responses when the
  // operator changes filters rapidly.
  const [ordersFullyLoaded, setOrdersFullyLoaded] = useState(false);
  const [ordersServerCount, setOrdersServerCount] = useState(null);
  const loadSeq = React.useRef(0);
  // Wave 3.99b — Cascade filter source: the UNFILTERED order universe
  // for the current company. Used purely to derive dropdown options
  // (Goldsmith / Customer / Stage / Status) so each dropdown only
  // shows values that would still produce visible results given the
  // OTHER active filters. Refreshed once per company and after any
  // mutation that changes the order set.
  const [masterList, setMasterList] = useState([]);
  const [goldsmiths, setGoldsmiths] = useState([]);
  const [purities, setPurities] = useState([]);
  // Wave 3.25 — Master list of Jewellery Types (per company).
  const [jewelleryTypes, setJewelleryTypes] = useState([]);
  // Wave 3.46 — Customer master + selected filter.
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState(() => {
    const base = { status: 'active', goldsmith_id: '', customer_id: '', stage: 'all', substage: 'all', search: '', pending_only: false, pending_materials: [], rm_status: 'all', reference_nos: [], cad_source: 'all', jewellery_type: '', date_from: '', date_to: '', design_no: '' };
    // Wave 3.99ac — Seed from deep-link query params when present.
    try {
      const qsCustomer = searchParams.get('customer_id');
      const qsStatus = searchParams.get('status');
      if (qsCustomer) base.customer_id = qsCustomer;
      if (qsStatus && ALLOWED_STATUS.has(qsStatus)) base.status = qsStatus;
      // Command Palette Page-No jump: ``?reference_no=1007`` (or a
      // comma-separated ``?reference_nos=1007,1008``) seeds the reference
      // filter and forces status=all so the target order(s) are visible
      // regardless of their current stage.
      const qsRef = searchParams.get('reference_no');
      const qsRefs = searchParams.get('reference_nos');
      const seededRefs = [];
      if (qsRef) seededRefs.push(qsRef);
      if (qsRefs) qsRefs.split(',').map((x) => x.trim()).filter(Boolean).forEach((x) => seededRefs.push(x));
      if (seededRefs.length) {
        base.reference_nos = Array.from(new Set(seededRefs));
        base.status = 'all';
      }
      // Command Palette free-text jump: ``?search=OF-123`` (alias ``?q=``)
      // seeds the order-no / title search box and forces status=all so the
      // matching order(s) are visible regardless of their current stage.
      const qsSearch = searchParams.get('search') || searchParams.get('q');
      if (qsSearch) {
        base.search = qsSearch;
        base.status = 'all';
      }
      // Design Master jump: ``?design_no=RD-102`` seeds the Design filter and
      // forces status=all so every order on that design is visible.
      const qsDesign = searchParams.get('design_no');
      if (qsDesign) {
        base.design_no = qsDesign;
        base.status = 'all';
      }
    } catch { /* ignore malformed params */ }
    return base;
  });
  // Wave 3.99ac — After seeding the filter from the URL, clear the query
  // string (replace, no history entry) so a later "Clear filters" or a
  // page refresh reflects the live filter state rather than re-hydrating
  // the stale deep-link.
  useEffect(() => {
    if (searchParams.get('customer_id') || searchParams.get('status')
        || searchParams.get('reference_no') || searchParams.get('reference_nos')
        || searchParams.get('search') || searchParams.get('q')
        || searchParams.get('design_no')) {
      setSearchParams({}, { replace: true });
    }
    /* eslint-disable-next-line */
  }, []);
  // Wave 3.92 — View mode toggle (table | grid). Grid is the default
  // (image-first browsing per user brief). Preference persisted per
  // browser in localStorage so switching to Table sticks across visits.
  const [view, setView] = useState(() => {
    try {
      const v = localStorage.getItem('orders_view');
      return v === 'table' ? 'table' : 'grid';
    } catch { return 'grid'; }
  });
  useEffect(() => {
    try { localStorage.setItem('orders_view', view); } catch { /* ignore */ }
  }, [view]);
  // Wave 3.99af — Saved Filter Views.
  // ``hadDeepLinkRef`` captures (on first render, BEFORE the strip effect
  // above clears them) whether the operator arrived via a deep-link
  // (?customer_id / ?status). A deep-link always wins over a saved default
  // for that navigation. ``autoAppliedDefaultRef`` guarantees the default
  // view auto-applies at most once — subsequent list reloads (after
  // save/delete) never re-hydrate the filter under the user's feet.
  const hadDeepLinkRef = React.useRef(
    !!(searchParams.get('customer_id') || searchParams.get('status')
       || searchParams.get('reference_no') || searchParams.get('reference_nos')
       || searchParams.get('search') || searchParams.get('q')
       || searchParams.get('design_no')),
  );
  const autoAppliedDefaultRef = React.useRef(false);
  const applySavedView = React.useCallback((snap, viewMode) => {
    const s = snap || {};
    setFilter({
      status: s.status ?? 'active',
      goldsmith_id: s.goldsmith_id ?? '',
      customer_id: s.customer_id ?? '',
      stage: s.stage ?? 'all',
      substage: s.substage ?? 'all',
      search: s.search ?? '',
      pending_only: !!s.pending_only,
      pending_materials: Array.isArray(s.pending_materials) ? s.pending_materials : [],
      rm_status: s.rm_status ?? 'all',
      reference_nos: Array.isArray(s.reference_nos) ? s.reference_nos : [],
      cad_source: s.cad_source ?? 'all',
      has_attachments: false,
      jewellery_type: s.jewellery_type ?? '',
      date_from: s.date_from ?? '',
      date_to: s.date_to ?? '',
      design_no: s.design_no ?? '',
    });
    if (viewMode === 'table' || viewMode === 'grid') setView(viewMode);
  }, []);
  const handleViewsLoaded = React.useCallback((rows) => {
    if (autoAppliedDefaultRef.current) return;   // only the very first load
    autoAppliedDefaultRef.current = true;
    if (hadDeepLinkRef.current) return;          // deep-link overrides default
    const def = (rows || []).find((r) => r.is_default);
    if (def) applySavedView(def.snapshot, def.view_mode);
  }, [applySavedView]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    goldsmith_id: '', jewellery_purity_id: '',
    title: '', description: '', estimated_completion: '',
    notes: '', special_comments: '', images: [], image_titles: [],
    // Wave 3.25 — Type of Jewellery (Necklace / Earrings / etc.). Optional.
    jewellery_type: '',
    // Wave 3.46 — Customer attribution. Optional.
    customer_id: '',
    // Wave 3.78 — Production Timeline SLA overrides + per-stage delay-notify recipients.
    priority: 'normal',
    sla_per_stage: {},
    delay_notify_by_stage: {},
    delay_notify_user_ids: [],
    // Wave 3.99ah — Global Order Raised notify list (notify-only).
    order_raised_notify_user_ids: [],
    // Wave 3.82 — Material Procurement config (SLA + notify recipients per material)
    procurement_config: {
      gold:        { sla_days: 3, notify_user_ids: [] },
      diamond:     { sla_days: 4, notify_user_ids: [] },
      color_stone: { sla_days: 4, notify_user_ids: [] },
      pearl:       { sla_days: 3, notify_user_ids: [] },
    },
    // Wave 3.83 — Per-order SLA tracking scope (both on by default).
    sla_scope: { timeline: true, procurement: true },
    // Reference-Number system — link to an existing reference at creation.
    link_existing_reference: false,
    existing_reference_no: null,
    // Design Master — mode: 'existing' picks a design; 'new' uploads an image
    // that becomes the Master image. ``design_id`` is the linked design;
    // ``design_new_image`` holds the uploaded data URL until submit.
    design_mode: 'existing',
    design_id: null,
    design_new_image: '',
    design_selected_no: '',
    // Wave 4.x — In-House CAD (default ticked). Untick when CAD is external.
    in_house_cad: true,
  });
  const fileRef = React.useRef(null);
  const designFileRef = React.useRef(null);

  // New Order — pick an image for a brand-new design (mode='new').
  const onDesignFile = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error(`${f.name} > 2MB, skipped`); if (designFileRef.current) designFileRef.current.value = ''; return; }
    const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
    setForm((p) => ({ ...p, design_new_image: dataUrl }));
    if (designFileRef.current) designFileRef.current.value = '';
  };
  // Phase 11 — Inline create dialogs + Save & Print PDF
  const [goldsmithDlgOpen, setGoldsmithDlgOpen] = useState(false);
  const [purityDlgOpen, setPurityDlgOpen] = useState(false);
  // Wave 3.25 — Inline "+ New Type" dialog state
  const [typeDlgOpen, setTypeDlgOpen] = useState(false);
  const [pdfDialog, setPdfDialog] = useState({ open: false, orderId: null, orderNo: null });
  // Wave 3.83 — SLA tracking toggle dialog state
  const [slaToggle, setSlaToggle] = useState({ open: false, order: null });
  // Wave 3.85 — Draft-mode SLA dialog for the New Order form
  const [slaDraftOpen, setSlaDraftOpen] = useState(false);
  // Wave 3.85 — Tracks whether the user has explicitly customised SLA in the
  // create-order draft. If false, the SLA defaults are auto-refreshed each
  // time the operator picks a new Jewellery Type. Set to true only after
  // the user hits "Apply SLA" in the dialog.
  const [slaCustomized, setSlaCustomized] = useState(false);
  // Wave 3.85 — Resolved SLA template snapshot (for the summary card).
  const [slaTemplateResolved, setSlaTemplateResolved] = useState({ stages: [], procurement_defaults: [] });

  const load = async () => {
    const params = { company_id: companyId };
    if (filter.status && filter.status !== 'all') params.status = filter.status;
    if (filter.goldsmith_id) params.goldsmith_id = filter.goldsmith_id;
    if (filter.customer_id) params.customer_id = filter.customer_id;  // Wave 3.46
    // Wave 3.68 — Stage + Substage filters
    if (filter.stage && filter.stage !== 'all') params.stage = filter.stage;
    if (filter.substage && filter.substage !== 'all') params.substage = filter.substage;
    // Wave 4.x — "Order created" date-range (IST calendar days, YYYY-MM-DD).
    // Applied server-side so it is accurate across the full paginated set.
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    // Design Master — filter to every order linked to a given Design Number
    // (server-side so it is accurate across the full paginated set).
    if (filter.design_no) params.design_no = filter.design_no;
    // Wave 4.x — PROGRESSIVE LOAD. Render the first page almost instantly, then
    // stream in the complete set so pages 2+ and all client-side filters work
    // on the full data. A cheap server count fills the pagination bar up front.
    const seq = ++loadSeq.current;
    setOrdersFullyLoaded(false);
    // (a) cheap total — fills the pagination bar while the list loads
    api.get('/orders/', { params: { ...params, count_only: true } })
      .then(({ data }) => {
        if (seq === loadSeq.current) {
          setOrdersServerCount(typeof data?.count === 'number' ? data.count : null);
        }
      })
      .catch(() => { if (seq === loadSeq.current) setOrdersServerCount(null); });
    // (b) fast first page (cover both 25 & 50 page sizes)
    try {
      const { data } = await api.get('/orders/', { params: { ...params, limit: 50 } });
      if (seq === loadSeq.current) setList(Array.isArray(data) ? data : []);
    } catch { if (seq === loadSeq.current) setList([]); }
    // (c) full set in the background — replaces the fast page when ready
    api.get('/orders/', { params })
      .then(({ data }) => {
        if (seq !== loadSeq.current) return;
        setList(Array.isArray(data) ? data : []);
        setOrdersFullyLoaded(true);
      })
      .catch(() => { if (seq === loadSeq.current) setOrdersFullyLoaded(true); });
  };
  // Wave 4.x — Inline edit of an order's Estimated Completion date from the
  // Grid card. Sends a partial PUT (only estimated_completion) and patches
  // the row locally so the card updates instantly without a full reload.
  const onUpdateCompletion = React.useCallback(async (orderId, dateStr) => {
    const value = dateStr || '';
    try {
      await api.put(`/orders/${orderId}`, { estimated_completion: value });
      setList((prev) => prev.map((o) => (o.id === orderId ? { ...o, estimated_completion: value || null } : o)));
      toast.success(value ? 'Estimated completion updated' : 'Estimated completion cleared');
    } catch (e) {
      toast.error(extractError(e, 'Could not update estimated completion'));
    }
  }, []);
  // Wave 3.99b — Fetch the UNFILTERED order universe for the current
  // company. Used ONLY to derive cascaded filter dropdown options so
  // each dropdown reveals just the values that would still produce
  // visible results given the OTHER active filters.
  const loadMaster = async () => {
    try {
      const { data } = await api.get('/orders/', { params: { company_id: companyId } });
      setMasterList(Array.isArray(data) ? data : []);
    } catch { setMasterList([]); }
  };
  const loadGs = async () => {
    // Sales roles have no goldsmith access (403) — fail soft so the Orders
    // page still renders (the goldsmith filter simply stays empty for them).
    try {
      const { data } = await api.get('/masters/goldsmiths', { params: { company_id: companyId } });
      setGoldsmiths(Array.isArray(data) ? data : []);
    } catch { setGoldsmiths([]); }
  };
  const loadPurities = async () => {
    try {
      const { data } = await api.get('/gold/purities', { params: { company_id: companyId } });
      setPurities(data || []);
    } catch { setPurities([]); }
  };
  // Wave 3.25 — Load company-level jewellery types (seeds defaults on first call).
  const loadJewelleryTypes = async () => {
    try {
      const { data } = await api.get('/jewellery-types', { params: { company_id: companyId } });
      setJewelleryTypes(Array.isArray(data) ? data : []);
    } catch { setJewelleryTypes([]); }
  };
  // Wave 3.46 — Load company-level customers (active only, archived excluded).
  const loadCustomers = async () => {
    try {
      const { data } = await api.get('/masters/customers', { params: { company_id: companyId } });
      setCustomers(Array.isArray(data) ? data : []);
    } catch { setCustomers([]); }
  };

  // Page-No filter options — loaded from a dedicated LIGHT endpoint (distinct
  // running-numbers) in parallel with the other filter dropdowns, so the
  // Page-No chip populates instantly instead of waiting for the heavy
  // unfiltered orders (`masterList`) download.
  const [refFilterOptions, setRefFilterOptions] = useState([]);
  const loadRefOptions = async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get('/orders/reference-filter-options', { params: { company_id: companyId } });
      setRefFilterOptions(Array.isArray(data) ? data : []);
    } catch { setRefFilterOptions([]); }
  };

  // Wave 3.99u — Pending requirements map. Keyed by ``order_id`` with
  // a Set of material_types the order still has pending. Sourced from
  // the same endpoint the Dashboard's Pending Requirements widget uses
  // so counts stay in perfect sync between the two surfaces.
  //  * Map is empty for buyers who cannot see any pending
  //    requirements (e.g. new-tenant companies) — that's a no-op for
  //    the filter machinery.
  //  * ``pendingMaterialScope`` mirrors ``material_scope`` from the
  //    payload so we can hide checkboxes for materials the buyer has
  //    zero visibility into.
  const [pendingMap, setPendingMap] = useState(new Map());
  const [pendingMaterialScope, setPendingMaterialScope] = useState([]);
  const loadPending = async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get('/dashboard/pending-requirements', {
        params: { company_id: companyId },
      });
      const m = new Map();
      for (const o of (data?.orders || [])) {
        const mats = new Set(Object.keys(o.groups || {}));
        if (mats.size) m.set(o.order_id, mats);
      }
      setPendingMap(m);
      setPendingMaterialScope(Array.isArray(data?.material_scope) ? data.material_scope : []);
    } catch {
      setPendingMap(new Map());
      setPendingMaterialScope([]);
    }
  };
  useEffect(() => {
    if (companyId) { load(); loadGs(); loadPurities(); loadJewelleryTypes(); loadCustomers(); loadRefOptions(); }
    /* eslint-disable-next-line */
  }, [companyId, filter.status, filter.goldsmith_id, filter.customer_id, filter.stage, filter.substage, filter.date_from, filter.date_to, filter.design_no]);
  // Wave 3.99b — Refresh cascade source when company changes. Kept
  // separate so filter changes never trigger a re-fetch of the master
  // universe. Also refreshed after mutations via the same list refresh
  // helpers below (loadAll/refreshAll where applicable).
  useEffect(() => {
    if (companyId) loadMaster();
    /* eslint-disable-next-line */
  }, [companyId]);

  // Wave 3.99u — Refresh the pending-requirements map alongside the
  // master list so any Pending / Material filter chip has fresh data
  // right after a company switch. Deliberately independent of the
  // per-filter effect so toggling status/goldsmith doesn't refetch.
  useEffect(() => {
    if (companyId) loadPending();
    /* eslint-disable-next-line */
  }, [companyId]);

  // Wave 3.85 — Auto-fill SLA defaults from the resolved template whenever
  // the operator opens the New Order dialog or changes the Jewellery Type.
  // We only overwrite defaults while the user has NOT explicitly customised
  // via the "Configure SLA" dialog — after Apply, the user's numbers win.
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/production-timeline/sla-template/resolve', {
          params: { company_id: companyId, jewellery_type: form.jewellery_type || '' },
        });
        if (cancelled) return;
        setSlaTemplateResolved({
          stages: data?.stages || [],
          procurement_defaults: data?.procurement_defaults || [],
        });
        if (!slaCustomized) {
          // Seed procurement config from resolved defaults; preserve any
          // notify_user_ids the user may have already picked.
          setForm((p) => {
            const nextProc = { ...(p.procurement_config || {}) };
            for (const row of data?.procurement_defaults || []) {
              const cur = nextProc[row.kind] || {};
              nextProc[row.kind] = {
                sla_days: Number(row.sla_days) || 0,
                notify_user_ids: cur.notify_user_ids || [],
              };
            }
            // Clear sla_per_stage so the resolved template's per-stage SLAs
            // are used at query time; user's per-stage overrides stay wiped
            // until they open the dialog and set explicit values.
            // Wave 3.99ah — Seed the global Order Raised notify list from the
            // resolved template default (operator can override in the dialog).
            return {
              ...p,
              procurement_config: nextProc,
              sla_per_stage: {},
              order_raised_notify_user_ids: [...(data?.order_raised_notify_user_ids || [])],
            };
          });
        }
      } catch {
        /* silent — the ProductionSlaSection will retry via its own fetch */
      }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [open, companyId, form.jewellery_type, slaCustomized]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const imgs = [];
    const t = [];
    for (const f of files) {
      if (f.size > 2 * 1024 * 1024) { toast.error(`${f.name} > 2MB, skipped`); continue; }
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
      imgs.push(dataUrl);
      // Seed each title with the filename base (strip extension).
      t.push((f.name || '').replace(/\.[^./\\]+$/, '').slice(0, 180));
    }
    setForm((p) => ({
      ...p,
      images: [...p.images, ...imgs],
      image_titles: [...(p.image_titles || []), ...t],
    }));
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeImage = (idx) => setForm((p) => ({
    ...p,
    images: p.images.filter((_, i) => i !== idx),
    image_titles: (p.image_titles || []).filter((_, i) => i !== idx),
  }));

  const setImageTitle = (idx, val) => setForm((p) => {
    const arr = [...(p.image_titles || Array(p.images.length).fill(''))];
    while (arr.length < p.images.length) arr.push('');
    arr[idx] = val;
    return { ...p, image_titles: arr };
  });

  const resetForm = () => {
    setForm({
      goldsmith_id: '', jewellery_purity_id: '',
      title: '', description: '', estimated_completion: '',
      notes: '', special_comments: '', images: [], image_titles: [],
      jewellery_type: '',
      customer_id: '',
      priority: 'normal',
      sla_per_stage: {},
      delay_notify_by_stage: {},
      delay_notify_user_ids: [],
      order_raised_notify_user_ids: [],
      procurement_config: {
        gold:        { sla_days: 3, notify_user_ids: [] },
        diamond:     { sla_days: 4, notify_user_ids: [] },
        color_stone: { sla_days: 4, notify_user_ids: [] },
        pearl:       { sla_days: 3, notify_user_ids: [] },
      },
      sla_scope: { timeline: true, procurement: true },
      link_existing_reference: false,
      existing_reference_no: null,
      design_mode: 'existing',
      design_id: null,
      design_new_image: '',
      design_selected_no: '',
      in_house_cad: true,
    });
    // Wave 3.85 — Reset the "customised" flag so the next draft auto-fills again.
    setSlaCustomized(false);
  };

  const create = async (alsoPrint = false) => {
    try {
      let designIdToUse = form.design_id;
      if (designEnabled) {
        if (form.design_mode === 'new') {
          if (!form.design_new_image) { toast.error('Please upload a design image or pick an existing design'); return; }
          const { data: d } = await api.post('/designs/', { image_data_url: form.design_new_image, company_id: companyId });
          designIdToUse = d.id;
        } else if (!form.design_id) {
          toast.error('Please select a design number'); return;
        }
      }
      // Strip the transient design UI fields; send only the resolved design_id.
      const { design_new_image, design_mode, design_selected_no, ...rest } = form;
      const payload = { ...rest, company_id: companyId, design_id: designEnabled ? designIdToUse : null };
      const { data } = await api.post('/orders/', payload);
      toast.success('Order created'); setOpen(false);
      resetForm();
      load();
      loadMaster();
      loadPending();
      refreshRefConfig();
      refreshDesignConfig();
      if (alsoPrint && data?.id) {
        setPdfDialog({ open: true, orderId: data.id, orderNo: data.order_no });
      }
    } catch (e) { toast.error(extractError(e, 'Order create failed')); }
  };

  // Wave 3.99b — Cascade filter machinery for the Orders list.
  // ``orderMatchesFilters`` returns true if an order (from the master
  // universe) passes the currently active filters; ``exclude`` lets a
  // caller skip a specific filter when computing that dropdown's
  // available options (so narrowing one filter never empties its own
  // choices).
  // Wave 4.10e — Side materials (diamond/color_stone/pearl) the buyer is
  // allowed to see, derived from the Pending-Requirements material scope.
  // Empty scope ⇒ full visibility (all 3 side materials). Used by the RM
  // buyer-status filter so a restricted buyer only ever filters on materials
  // they can actually see.
  const rmVisibleSideMaterialKeys = React.useMemo(() => {
    const alias = { diamonds: 'diamond', color_stones: 'color_stone', pearls: 'pearl' };
    const scope = new Set((pendingMaterialScope || []).map((s) => alias[s] || s));
    const sideKeys = RM_SIDE_MATERIALS.map((m) => m.key);
    if (!scope.size) return new Set(sideKeys);
    return new Set(sideKeys.filter((k) => scope.has(k)));
  }, [pendingMaterialScope]);

  const LEGACY_STAGES = React.useMemo(
    () => new Set(['Issued', 'In Production', 'Quality Check', 'Received Back', 'Completed', 'Cancelled']),
    [],
  );
  const orderMatchesFilters = React.useCallback((o, exclude = {}) => {
    // Status bucket (mirrors the backend logic in list_orders).
    if (!exclude.status && filter.status && filter.status !== 'all') {
      const stage = o.stage || '';
      const hold = o.hold_status;
      if (filter.status === 'active') {
        if (stage === 'Completed' || stage === 'Cancelled') return false;
        if (hold && hold !== 'active') return false;
      } else if (filter.status === 'on_hold') {
        if (hold !== 'on_hold') return false;
      } else if (filter.status === 'completed') {
        if (stage !== 'Completed') return false;
      } else if (filter.status === 'cancelled') {
        if (stage !== 'Cancelled') return false;
      } else if (filter.status === 'open') {
        if (stage === 'Completed' || stage === 'Cancelled') return false;
      }
    }
    if (!exclude.goldsmith && filter.goldsmith_id) {
      if ((o.goldsmith_id || '') !== filter.goldsmith_id) return false;
    }
    if (!exclude.customer && filter.customer_id) {
      if ((o.customer_id || '') !== filter.customer_id) return false;
    }
    if (!exclude.stage && filter.stage && filter.stage !== 'all') {
      if (LEGACY_STAGES.has(filter.stage)) {
        if ((o.stage || '') !== filter.stage) return false;
      } else if (filter.stage.startsWith('cad_')) {
        // Wave 4.10a — CAD development sub-stage (Not Allotted / WIP / Final).
        const sub = cadInternalSubstage(o);
        if (!sub || `cad_${sub}` !== filter.stage) return false;
      } else {
        if (!activeStepsMatch(o.active_steps, filter.stage)) return false;
      }
    }
    if (!exclude.search && filter.search && filter.search.trim()) {
      const s = filter.search.trim().toLowerCase();
      const hay = `${o.order_no || ''} ${o.title || ''}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    // Wave 4.10c — Jewellery Type filter (replaces the old Files toggle).
    if (!exclude.jewellery_type && filter.jewellery_type) {
      if ((o.jewellery_type || '') !== filter.jewellery_type) return false;
    }
    // Wave 3.99u — Pending-requirements filter. The material list is
    // an OR set (order kept if it has pending in ANY selected
    // material), and only applies when either the master toggle
    // ``pending_only`` is on OR ``pending_materials`` is non-empty.
    if (!exclude.pending) {
      const wantsMaterials = Array.isArray(filter.pending_materials) && filter.pending_materials.length > 0;
      if (filter.pending_only || wantsMaterials) {
        const mats = pendingMap.get(o.id);
        if (!mats || mats.size === 0) return false;
        if (wantsMaterials) {
          const hit = filter.pending_materials.some((m) => mats.has(m));
          if (!hit) return false;
        }
      }
    }
    // Wave 4.10e — Raw Material buyer-status filter (Order Pending / Order
    // Raised / Material Collected). An order matches if ANY side-material
    // requirement that is still pending (not issued) sits in the chosen
    // procurement state. Scoped to the buyer's visible side materials, and
    // narrowed further to the selected pending materials when present.
    if (!exclude.rm_status && filter.rm_status && filter.rm_status !== 'all') {
      const selected = (filter.pending_materials || []).filter((m) => rmVisibleSideMaterialKeys.has(m));
      const consider = selected.length ? selected : Array.from(rmVisibleSideMaterialKeys);
      if (!consider.length) return false;
      const hit = consider.some((kind) => rmBuyerStatusOf(o, kind) === filter.rm_status);
      if (!hit) return false;
    }
    return true;
  }, [filter.status, filter.goldsmith_id, filter.customer_id, filter.stage, filter.search, filter.pending_only, filter.pending_materials, filter.rm_status, filter.jewellery_type, pendingMap, rmVisibleSideMaterialKeys, LEGACY_STAGES]);

  // Cascaded option sets — each excludes its own filter so that
  // narrowing does not empty the dropdown itself.
  const cascadedGoldsmithIds = React.useMemo(() => {
    const set = new Set();
    for (const o of masterList) if (orderMatchesFilters(o, { goldsmith: true }) && o.goldsmith_id) set.add(o.goldsmith_id);
    return set;
  }, [masterList, orderMatchesFilters]);
  const cascadedCustomerIds = React.useMemo(() => {
    const set = new Set();
    for (const o of masterList) if (orderMatchesFilters(o, { customer: true }) && o.customer_id) set.add(o.customer_id);
    return set;
  }, [masterList, orderMatchesFilters]);
  const cascadedStageValues = React.useMemo(() => {
    const set = new Set();
    for (const o of masterList) {
      if (!orderMatchesFilters(o, { stage: true })) continue;
      if (o.stage) set.add(o.stage);
      for (const k of activeStepKeys(o.active_steps)) set.add(k);
      const cs = cadInternalSubstage(o);
      if (cs) set.add(`cad_${cs}`);
    }
    return set;
  }, [masterList, orderMatchesFilters]);
  // Wave 4.10c — Jewellery Type cascaded option set (types present among
  // orders that satisfy every OTHER active filter).
  const cascadedJewelleryTypes = React.useMemo(() => {
    const set = new Set();
    for (const o of masterList) {
      if (!orderMatchesFilters(o, { jewellery_type: true })) continue;
      if (o.jewellery_type) set.add(o.jewellery_type);
    }
    if (filter.jewellery_type) set.add(filter.jewellery_type);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [masterList, orderMatchesFilters, filter.jewellery_type]);
  const cascadedStatusValues = React.useMemo(() => {
    const set = new Set();
    // "all" is always available (no-op filter).
    set.add('all');
    for (const o of masterList) {
      if (!orderMatchesFilters(o, { status: true })) continue;
      const stage = o.stage || '';
      const hold = o.hold_status;
      // active: not completed, not cancelled, not on_hold
      if (stage !== 'Completed' && stage !== 'Cancelled' && (!hold || hold === 'active')) set.add('active');
      // on_hold
      if (hold === 'on_hold') set.add('on_hold');
      // completed
      if (stage === 'Completed') set.add('completed');
      // cancelled
      if (stage === 'Cancelled') set.add('cancelled');
      // open: not completed, not cancelled (regardless of hold)
      if (stage !== 'Completed' && stage !== 'Cancelled') set.add('open');
    }
    return set;
  }, [masterList, orderMatchesFilters]);

  // Cascaded, sorted lists ready for dropdown rendering. Always
  // include the CURRENTLY selected value so the operator can un-select
  // it even if the cascade would otherwise hide it.
  const cascadedGoldsmiths = React.useMemo(() => {
    if (!masterList.length) return goldsmiths; // master not yet loaded → don't collapse.
    return (goldsmiths || []).filter(
      (g) => cascadedGoldsmithIds.has(g.id) || g.id === filter.goldsmith_id,
    );
  }, [goldsmiths, cascadedGoldsmithIds, filter.goldsmith_id, masterList.length]);
  const cascadedCustomers = React.useMemo(() => {
    if (!masterList.length) return customers;
    return (customers || []).filter(
      (c) => cascadedCustomerIds.has(c.id) || c.id === filter.customer_id,
    );
  }, [customers, cascadedCustomerIds, filter.customer_id, masterList.length]);

  // Wave 3.99u — Cascaded pending metrics.
  //  * ``pendingOrderCount`` — how many currently viable orders (i.e.
  //    orders that already pass every OTHER filter) still have pending
  //    requirements. Surfaces on the "Only orders with pending
  //    requirements" toggle in the Pending popover.
  //  * ``pendingMaterialCounts`` — per-material tally of pending orders
  //    that would pass every OTHER filter, so buyers see how many
  //    orders each checkbox would keep in view.
  //  * ``pendingMaterialsAvailable`` — the set of materials that
  //    actually appear as pending on the viable set. Materials with a
  //    zero count are hidden from the popover to avoid dead options.
  const pendingOrderCount = React.useMemo(() => {
    let c = 0;
    for (const o of masterList) {
      if (!orderMatchesFilters(o, { pending: true })) continue;
      const mats = pendingMap.get(o.id);
      if (mats && mats.size) c += 1;
    }
    return c;
  }, [masterList, orderMatchesFilters, pendingMap]);
  const pendingMaterialCounts = React.useMemo(() => {
    const counts = new Map();
    for (const o of masterList) {
      if (!orderMatchesFilters(o, { pending: true })) continue;
      const mats = pendingMap.get(o.id);
      if (!mats) continue;
      mats.forEach((m) => { counts.set(m, (counts.get(m) || 0) + 1); });
    }
    return counts;
  }, [masterList, orderMatchesFilters, pendingMap]);
  // Materials the buyer can actually pick — must be BOTH visible in
  // their material scope AND present in the current viable pending
  // set. Falls back to the full ordered material list when the master
  // list hasn't loaded yet so the popover renders sensible defaults.
  const pendingMaterialsAvailable = React.useMemo(() => {
    const scope = new Set(pendingMaterialScope);
    // Alias: dashboard payload uses ``diamonds`` / ``color_stones`` /
    // ``pearls`` as the material_scope slugs; the group keys are the
    // singular form (``diamond`` / ``color_stone`` / ``pearl``). Map
    // the plural → singular so the checkboxes render correctly.
    const scopeSingular = new Set();
    scope.forEach((s) => {
      const map = { diamonds: 'diamond', color_stones: 'color_stone', pearls: 'pearl', gold: 'gold' };
      scopeSingular.add(map[s] || s);
    });
    return RAW_MATERIALS.filter((m) => {
      // Hide if outside the buyer's material scope.
      if (scope.size && !scopeSingular.has(m.key)) return false;
      // Hide if no pending orders in this material after other
      // filters are applied.
      if (masterList.length && !(pendingMaterialCounts.get(m.key) || 0)) return false;
      return true;
    });
  }, [pendingMaterialScope, pendingMaterialCounts, masterList.length]);

  // Wave 3.99aa — Hoisted client-side filter used by both the header
  // count pill and the Table/Grid renderers. Mirrors the logic that
  // previously lived inside the render IIFE (search + pending-only +
  // pending-materials chip filter). Keeping a single source of truth
  // guarantees the pill count and the visible rows never drift.
  const filteredOrders = React.useMemo(() => {
    const needle = (filter.search || '').trim().toLowerCase();
    const wantsPending = filter.pending_only || (filter.pending_materials || []).length > 0;
    const wantsMats = (filter.pending_materials || []).length > 0;
    const wantsRmStatus = filter.rm_status && filter.rm_status !== 'all';
    const rmSelected = (filter.pending_materials || []).filter((m) => rmVisibleSideMaterialKeys.has(m));
    const rmConsider = rmSelected.length ? rmSelected : Array.from(rmVisibleSideMaterialKeys);
    // Reference numbers may be stored as int OR string depending on the
    // company's numbering config; deep-links (Command Palette / clickable
    // pill) always arrive as strings. Compare on the string form so both
    // the multi-select dropdown and deep-links match reliably.
    const refSet = new Set((filter.reference_nos || []).map((r) => String(r)));
    return list.filter((o) => {
      if (needle) {
        // Command Palette + inline search now also match customer name and
        // the Page/Reference number, so typing a customer or page no filters
        // the order form directly.
        const hay = `${o.order_no || ''} ${o.title || ''} ${o.customer_name || ''} ${o.reference_no ?? ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (wantsPending) {
        const mats = pendingMap.get(o.id);
        if (!mats || mats.size === 0) return false;
        if (wantsMats && !filter.pending_materials.some((m) => mats.has(m))) return false;
      }
      // Wave 4.10e — Raw Material buyer-status filter (client-side, mirrors
      // ``orderMatchesFilters``). Keep orders with a pending side-material
      // requirement sitting in the chosen procurement state.
      if (wantsRmStatus) {
        if (!rmConsider.length) return false;
        if (!rmConsider.some((kind) => rmBuyerStatusOf(o, kind) === filter.rm_status)) return false;
      }
      // Reference-Number multi-select filter (client-side).
      if (refSet.size > 0 && !refSet.has(String(o.reference_no))) return false;
      // Wave 4.x — CAD source filter. External = in_house_cad === false;
      // In-House = anything else (true or legacy/undefined).
      if (filter.cad_source === 'external' && o.in_house_cad !== false) return false;
      if (filter.cad_source === 'in_house' && o.in_house_cad === false) return false;
      // Wave 4.10c — Jewellery Type filter (replaced the old "Files" toggle).
      if (filter.jewellery_type && (o.jewellery_type || '') !== filter.jewellery_type) return false;
      return true;
    });
  }, [list, filter.search, filter.pending_only, filter.pending_materials, filter.rm_status, filter.reference_nos, filter.cad_source, filter.jewellery_type, pendingMap, rmVisibleSideMaterialKeys]);

  // Reference-Number system — distinct reference numbers present in the
  // current company's order universe (for the filter dropdown options).
  const referenceOptions = React.useMemo(() => {
    if (!refEnabled) return [];
    // Prefer the dedicated light endpoint (loads in parallel, independent of
    // the heavy masterList). Fall back to master-derived while it's in flight.
    if (refFilterOptions && refFilterOptions.length) return refFilterOptions;
    const set = new Set();
    for (const o of masterList) {
      if (o.reference_no != null) set.add(o.reference_no);
    }
    return Array.from(set);
  }, [refEnabled, refFilterOptions, masterList]);

  // Distinct running-numbers present in the CURRENTLY visible orders — powers
  // the "N pages" pill next to the order count.
  const filteredReferenceCount = React.useMemo(() => {
    if (!refEnabled) return 0;
    const set = new Set();
    for (const o of filteredOrders) {
      if (o.reference_no != null) set.add(o.reference_no);
    }
    return set.size;
  }, [refEnabled, filteredOrders]);

  // Wave 4.x — PAGINATION (replaces grow-on-scroll). Filters/search run over
  // the FULL loaded order set (``filteredOrders``); we then render only the
  // current page so a company with hundreds/thousands of orders paints fast.
  // Operators pick 25 or 50 per page (default 25) and can jump to any page.
  const PAGE_SIZE_OPTIONS = [25, 50];
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  // Reset to the first page when the FILTER/search/company/page-size changes —
  // deliberately NOT when the progressive load streams more rows in (that would
  // yank an operator who already paged ahead back to page 1).
  useEffect(() => { setPage(1); }, [
    companyId, pageSize,
    filter.status, filter.goldsmith_id, filter.customer_id, filter.stage, filter.substage,
    filter.search, filter.pending_only, filter.pending_materials, filter.rm_status, filter.reference_nos, filter.cad_source, filter.jewellery_type,
  ]);
  // Wave 4.x — Progressive-load aware pagination.
  // Client-side filters (text search, pending chips, page-no) narrow ``list``
  // in memory, so they NEED the complete set to be accurate. While one is
  // active and the full set hasn't arrived, we show a loading state instead of
  // partial (misleading) results.
  const clientFilterActive =
    Boolean((filter.search || '').trim()) ||
    Boolean(filter.pending_only) ||
    (filter.pending_materials || []).length > 0 ||
    (filter.reference_nos || []).length > 0 ||
    (filter.cad_source && filter.cad_source !== 'all') ||
    Boolean(filter.jewellery_type);
  // Total: exact once fully loaded; otherwise use the cheap server count for the
  // (server-filtered) view so the bar shows the real page count up front.
  const ordersTotal = ordersFullyLoaded
    ? filteredOrders.length
    : (!clientFilterActive && ordersServerCount != null ? ordersServerCount : filteredOrders.length);
  const totalKnown = ordersFullyLoaded || (!clientFilterActive && ordersServerCount != null);
  const totalPages = Math.max(1, Math.ceil(ordersTotal / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, ordersTotal);
  const pagedOrders = filteredOrders.slice(pageStart, pageEnd);
  // Show a loading placeholder (instead of the list) when we can't yet render
  // an accurate page: a client filter is active pre-full-load, or the operator
  // paged past the rows in the fast first batch.
  const awaitingFullData = !ordersFullyLoaded && (clientFilterActive || pageStart >= filteredOrders.length);
  const [pageJump, setPageJump] = useState('');
  const goToPage = (p) => {
    const n = Math.min(Math.max(1, Math.floor(Number(p) || 1)), totalPages);
    setPage(n);
    setPageJump('');
  };

  // Wave 4.x — Context-aware bulk toolbar. We resolve the currently-selected
  // orders (selection persists across pages) against the loaded order set to
  // decide which batch actions make sense:
  //   • Resume shows only if at least one selected order is ON HOLD.
  //   • Hold shows only if at least one selected order is HOLDABLE (active &
  //     not Completed/Cancelled).
  //   • Delete is DISABLED if any selected order has raw material issued
  //     (deleting it would orphan those goldsmith issues).
  const orderLookup = React.useMemo(() => {
    const m = new Map();
    for (const o of (masterList || [])) m.set(o.id, o);
    for (const o of (list || [])) m.set(o.id, o); // fresher state wins
    return m;
  }, [masterList, list]);
  const selectedOrders = React.useMemo(
    () => Array.from(selectedIds).map((sid) => orderLookup.get(sid)).filter(Boolean),
    [selectedIds, orderLookup],
  );
  const isTerminalOrder = (o) => o.stage === 'Completed' || o.stage === 'Cancelled';
  const anyOnHold = selectedOrders.some((o) => (o.hold_status || 'active') === 'on_hold');
  const anyHoldable = selectedOrders.some((o) => (o.hold_status || 'active') !== 'on_hold' && !isTerminalOrder(o));
  const issuedSelectedCount = selectedOrders.filter((o) => o.has_material_issued).length;
  const anyIssuedMaterial = issuedSelectedCount > 0;
  // Running "across pages" context for the sticky selection bar so operators
  // can track picks while paging.
  const selectedOnThisPageCount = pagedOrders.reduce((n, o) => n + (selectedIds.has(o.id) ? 1 : 0), 0);
  const selectionSpansPages = selectedIds.size > selectedOnThisPageCount;

  return (
    <div>
      <div className="sticky-filter-bar mb-3 rounded-lg px-3" data-testid="orders-filter-bar">
        <div className="flex flex-col gap-2.5" data-testid="orders-sticky-toolbar-container">
          {/* ────────────────────────────────────────────────────────────
              Wave 4.0 — Orders filter toolbar redesign.
              Three calm bands inside the sticky bar:
                1) Header row  — page identity + primary actions.
                2) Filter bar  — grouped, bordered surface of filters.
                3) Chip row    — removable applied-filter chips.
              Page actions (View / Saved Views / New Order) are lifted out
              of the filter cluster so the bar reads clean and the primary
              action stays prominent. Purely a layout refactor — every
              control keeps its original wiring + data-testid. */}

          {/* ── Band 1 — Header row ─────────────────────────────────────── */}
          <div
            className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
            data-testid="orders-toolbar-header-row"
          >
            <div className="flex items-center gap-2.5">
              <div className="text-[18px] sm:text-[20px] font-semibold tracking-[-0.01em] text-[var(--ink-navy)]">
                All Orders
              </div>
              {/* Filtered count pill — orders visible after search + filters + chips. */}
              <span
                className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-white text-slate-600 border border-[var(--divider)] tabular-nums"
                data-testid="orders-filtered-count-pill"
              >
                {totalKnown
                  ? `${ordersTotal} ${ordersTotal === 1 ? 'order' : 'orders'}`
                  : `${filteredOrders.length}… orders`}
              </span>
              {/* Running-number pill — distinct Page/Job/Order Nos among the visible orders.
                  Clickable: filters the list to exactly these Page Nos (toggle). */}
              {refEnabled && (
                <button
                  type="button"
                  onClick={() => {
                    setFilter((f) => {
                      if ((f.reference_nos || []).length > 0) {
                        return { ...f, reference_nos: [] };
                      }
                      const set = new Set();
                      for (const o of filteredOrders) {
                        if (o.reference_no != null && o.reference_no !== '') set.add(String(o.reference_no));
                      }
                      return { ...f, reference_nos: Array.from(set) };
                    });
                  }}
                  title={(filter.reference_nos || []).length > 0
                    ? `Clear ${refLabel.replace(/\.$/, '')} filter`
                    : `Filter to these ${refLabel.replace(/\.$/, '')}s`}
                  aria-pressed={(filter.reference_nos || []).length > 0}
                  className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-[11px] font-medium tabular-nums cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
                    (filter.reference_nos || []).length > 0
                      ? 'bg-indigo-600 text-white border border-indigo-600 hover:bg-indigo-700'
                      : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100'
                  }`}
                  data-testid="orders-reference-count-pill"
                >
                  {`${filteredReferenceCount} ${refLabel.replace(/\.$/, '')}${filteredReferenceCount === 1 ? '' : 's'}`}
                </button>
              )}
              {/* Design Master filter — active chip; click to clear and see all orders again. */}
              {filter.design_no && (
                <button
                  type="button"
                  onClick={() => setFilter((f) => ({ ...f, design_no: '' }))}
                  title="Clear Design filter"
                  className="inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-violet-600 text-white border border-violet-600 hover:bg-violet-700 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                  data-testid="orders-design-filter-chip"
                >
                  <Palette size={11} /> Design: {filter.design_no}
                  <XIcon size={12} className="ml-0.5 opacity-80" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end" data-testid="orders-toolbar-actions">
              {/* Wave 3.92 — View mode toggle (Table | Grid). Preference persists in localStorage. */}
              <div
                className="inline-flex items-center h-9 rounded-md border border-[var(--divider)] bg-white p-0.5"
                role="tablist"
                aria-label="Orders view mode"
                data-testid="orders-view-toggle"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === 'table'}
                  onClick={() => setView('table')}
                  className={`inline-flex items-center gap-1 px-2.5 h-8 text-[12px] rounded transition-colors ${
                    view === 'table'
                      ? 'bg-[var(--ink-navy)] text-white'
                      : 'text-slate-600 hover:text-[var(--ink-navy)] hover:bg-slate-50'
                  }`}
                  data-testid="orders-view-toggle-table"
                  title="Show orders as a table"
                >
                  <TableIcon size={13} /> Table
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === 'grid'}
                  onClick={() => setView('grid')}
                  className={`inline-flex items-center gap-1 px-2.5 h-8 text-[12px] rounded transition-colors ${
                    view === 'grid'
                      ? 'bg-[var(--ink-navy)] text-white'
                      : 'text-slate-600 hover:text-[var(--ink-navy)] hover:bg-slate-50'
                  }`}
                  data-testid="orders-view-toggle-grid"
                  title="Show orders as image-first cards"
                >
                  <LayoutGrid size={13} /> Grid
                </button>
              </div>
              {/* Wave 3.99af — Saved Filter Views (save / apply / default). */}
              <SavedViewsMenu
                companyId={companyId}
                filter={filter}
                view={view}
                goldsmiths={goldsmiths}
                customers={customers}
                onApply={applySavedView}
                onViewsLoaded={handleViewsLoaded}
                hideLabel
              />
              {!isSalesRole && <Button className="luxury-button h-9" onClick={() => setOpen(true)} data-testid="orders-add-button"><Plus size={14} className="mr-1.5" /> New Order</Button>}
            </div>
          </div>

          {/* ── Band 2 — Filter toolbar surface ─────────────────────────── */}
          <div
            className="rounded-lg border border-[var(--divider)] bg-white/90 px-2.5 py-2 shadow-sm"
            data-testid="orders-toolbar-filters-row"
          >
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
              {/* Wave 3.85 — Universal search (Order No + Title). Dominant control. */}
              <div className="relative w-full lg:w-56 lg:flex-shrink-0">
                <SearchIcon size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={filter.search}
                  onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                  placeholder="Search Order No or Title…"
                  className="w-full h-9 pl-9"
                  data-testid="orders-search-input"
                />
              </div>

              {/* Divider: Search | Filters (desktop only). */}
              <div className="hidden lg:block h-7 w-px bg-[var(--divider)]" aria-hidden="true" />

              {/* Filter controls — People group, then Workflow group.
                  Wave 4.x — single-line guarantee: on desktop (lg+) the group
                  never wraps into a shabby second row; it stays on one line and
                  scrolls horizontally only if the viewport is too narrow to hold
                  every control. Children keep their width (no squish) via
                  shrink-0. Mobile keeps the stacked flex-wrap behaviour. */}
              <div className="flex flex-1 flex-wrap items-center gap-2 lg:flex-nowrap lg:overflow-x-auto lg:[&>*]:shrink-0">
                {/* People — Goldsmith */}
                <div className="w-full sm:w-[195px]">
                  <SearchableSelect
                    items={[{ id: 'all', name: 'All goldsmiths' }, ...cascadedGoldsmiths]}
                    value={filter.goldsmith_id || 'all'}
                    onValueChange={(v) => setFilter({ ...filter, goldsmith_id: !v || v === 'all' ? '' : v })}
                    itemToLabel={(g) => g.name}
                    searchKeys={['name', 'phone', 'email']}
                    placeholder="All goldsmiths"
                    searchPlaceholder="Search goldsmith…"
                    leadingIcon={<Users size={14} />}
                    testId="orders-goldsmith-filter"
                  />
                </div>
                {/* People — Customer */}
                <div className="w-full sm:w-[210px]">
                  <CustomerCombobox
                    customers={cascadedCustomers}
                    value={filter.customer_id}
                    onChange={(id) => setFilter({ ...filter, customer_id: id || '' })}
                    companyId={companyId}
                    onCustomerCreated={loadCustomers}
                    placeholder="All customers"
                    testIdPrefix="orders-customer-filter"
                  />
                </div>

                {/* Divider: People | Workflow (desktop only). */}
                <div className="hidden lg:block h-7 w-px bg-[var(--divider)]" aria-hidden="true" />
                {/* Workflow — Status */}
                <div className="w-full sm:w-[150px]">
                  <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
                    <SelectTrigger className="w-full h-9" data-testid="orders-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      {/* Wave 3.99b — Cascade: only render a status bucket if the
                          unfiltered universe (minus this filter) contains at
                          least one order in that bucket. Currently selected
                          value is always kept visible so it remains removable. */}
                      {[
                        { v: 'active',    label: 'Active' },
                        { v: 'on_hold',   label: 'On Hold' },
                        { v: 'completed', label: 'Completed' },
                        { v: 'cancelled', label: 'Cancelled' },
                        { v: 'open',      label: 'All Open' },
                        { v: 'all',       label: 'All' },
                      ].filter((it) => (
                        // On Hold & Cancelled are ALWAYS offered so operators can
                        // always filter to those buckets; the rest cascade to
                        // avoid dead options.
                        it.v === 'on_hold' || it.v === 'cancelled'
                        || !masterList.length || cascadedStatusValues.has(it.v) || filter.status === it.v
                      )).map((it) => (
                        <SelectItem key={it.v} value={it.v}>{it.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Workflow — Stage. Wave 3.68 / 3.90 — includes checklist step
                    keys (cad/cam/chamka/ghat/raw_material/setting/qc/delivery) in
                    addition to legacy top-level stages. Wave 3.99b cascade logic
                    preserved (only viable values render). */}
                <div className="w-full sm:w-[195px]">
                  <Select
                    value={filter.stage}
                    onValueChange={(v) => setFilter((f) => ({ ...f, stage: v, substage: 'all' }))}
                  >
                    <SelectTrigger className="w-full h-9" data-testid="orders-stage-filter"><SelectValue placeholder="Stage" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All stages</SelectItem>
                      {(() => {
                        const inCascade = (v) => !masterList.length || cascadedStageValues.has(v) || filter.stage === v;
                        const legacyItems = [
                          { v: 'Issued',        label: 'Issued' },
                          { v: 'In Production', label: 'In Production' },
                          { v: 'Quality Check', label: 'Quality Check' },
                          { v: 'Received Back', label: 'Received Back' },
                          { v: 'Completed',     label: 'Completed' },
                          { v: 'Cancelled',     label: 'Cancelled' },
                        ].filter((it) => inCascade(it.v));
                        const stepItems = [
                          { v: 'cad',              label: 'CAD Active' },
                          { v: 'cad_not_allotted', label: 'CAD · Not Allotted' },
                          { v: 'cad_wip',          label: 'CAD · WIP' },
                          { v: 'cam',          label: 'CAM Active' },
                          { v: 'chamka',       label: 'Chamka Active' },
                          { v: 'ghat_pending_gold_order', label: 'Ghat - Pending Gold Order' },
                          { v: 'ghat_waiting', label: 'Ghat - Waiting For Gold' },
                          { v: 'ghat',         label: 'Ghat Active' },
                          { v: 'raw_material', label: 'Raw Material Active' },
                          { v: 'setting',      label: 'Setting Active' },
                          { v: 'qc',           label: 'Quality Check Active' },
                          { v: 'delivery',     label: 'Delivery Active' },
                        ].filter((it) => inCascade(it.v));
                        return (
                          <>
                            {legacyItems.length > 0 && (
                              <SelectItem value="__legacy_header__" disabled>─ Order Stage ─</SelectItem>
                            )}
                            {legacyItems.map((it) => (
                              <SelectItem key={it.v} value={it.v}>{it.label}</SelectItem>
                            ))}
                            {stepItems.length > 0 && (
                              <SelectItem value="__checklist_header__" disabled>─ Production Checklist Step ─</SelectItem>
                            )}
                            {stepItems.map((it) => (
                              <SelectItem key={it.v} value={it.v}>{it.label}</SelectItem>
                            ))}
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                {/* Workflow — CAD source (In-House vs External). Wave 4.x */}
                <div className="w-full sm:w-[150px]">
                  <Select
                    value={filter.cad_source || 'all'}
                    onValueChange={(v) => setFilter((f) => ({ ...f, cad_source: v }))}
                  >
                    <SelectTrigger className="w-full h-9" data-testid="orders-cad-source-filter">
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        <Cpu size={14} className="text-slate-400 shrink-0" />
                        <SelectValue placeholder="CAD" />
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All CAD</SelectItem>
                      <SelectItem value="in_house">In-House CAD</SelectItem>
                      <SelectItem value="external">External CAD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Wave 4.10c — Jewellery Type filter (replaced the Files toggle).
                    Cascades with the other filters; options reflect the current
                    filtered set. */}
                <div className="min-w-[150px]">
                  <Select
                    value={filter.jewellery_type || '__all__'}
                    onValueChange={(v) => setFilter((f) => ({ ...f, jewellery_type: v === '__all__' ? '' : v }))}
                  >
                    <SelectTrigger className="w-full h-9" data-testid="orders-jewellery-type-filter">
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        <Palette size={14} className="text-slate-400 shrink-0" />
                        <SelectValue placeholder="Jewellery Type" />
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Types</SelectItem>
                      {cascadedJewelleryTypes.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Wave 3.99u — Workflow — Pending Requirements filter. Own
                    inline popover (never nested). Gates the list to orders with
                    unfulfilled requirements + optional raw-material multi-select
                    scoped to the buyer's material visibility. */}
                <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 h-9 rounded-md border px-3 text-[12.5px] transition-colors ${
                    filter.pending_only || (filter.pending_materials || []).length || (filter.rm_status && filter.rm_status !== 'all')
                      ? 'border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100'
                      : 'border-[var(--divider)] bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid="orders-pending-filter-trigger"
                  title="Filter to orders with pending requirements"
                >
                  <ClipboardList size={13} />
                  <span>
                    {(filter.rm_status && filter.rm_status !== 'all')
                      ? (RM_STATUS_LABELS[filter.rm_status] || filter.rm_status)
                      : (filter.pending_only || (filter.pending_materials || []).length
                        ? (
                          (filter.pending_materials || []).length
                            ? `Pending · ${(filter.pending_materials || []).length} material${(filter.pending_materials || []).length === 1 ? '' : 's'}`
                            : 'Pending · All materials'
                        )
                        : 'Any')}
                  </span>
                  <ChevronDown size={12} className="opacity-60" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-72 p-3"
                align="start"
                data-testid="orders-pending-filter-content"
              >
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={filter.pending_only}
                    onCheckedChange={(chk) => setFilter((f) => ({
                      ...f,
                      pending_only: !!chk,
                      // Turning the master toggle OFF also clears
                      // any material sub-selection so the chip row
                      // never lingers with orphan Material chips.
                      pending_materials: chk ? f.pending_materials : [],
                    }))}
                    data-testid="orders-pending-only-toggle"
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">
                      Only orders with pending requirements
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {masterList.length
                        ? `${pendingOrderCount} matching order${pendingOrderCount === 1 ? '' : 's'}`
                        : 'Loading pending counts…'}
                    </div>
                  </div>
                </label>

                {/* Wave 3.99u — Raw material multi-select. Only
                    surfaces when the master toggle is ON to match the
                    UX brief ("once selected, they can go on further
                    selection of Raw Materials"). Hidden entirely when
                    no materials qualify in the buyer's scope. */}
                {filter.pending_only && pendingMaterialsAvailable.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                        Raw Materials
                      </div>
                      {(filter.pending_materials || []).length > 0 && (
                        <button
                          type="button"
                          onClick={() => setFilter((f) => ({ ...f, pending_materials: [] }))}
                          className="text-[11px] text-slate-500 hover:text-slate-800 underline"
                          data-testid="orders-pending-materials-clear"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">
                      Keep orders pending in any of the selected materials.
                    </div>
                    <div className="grid grid-cols-1 gap-1.5">
                      {pendingMaterialsAvailable.map((m) => {
                        const active = (filter.pending_materials || []).includes(m.key);
                        const count = pendingMaterialCounts.get(m.key) || 0;
                        return (
                          <label
                            key={m.key}
                            className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 cursor-pointer border ${
                              active ? 'bg-sky-50 border-sky-200' : 'border-transparent hover:bg-slate-50'
                            }`}
                            data-testid={`orders-pending-material-${m.key}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Checkbox
                                checked={active}
                                onCheckedChange={(chk) => setFilter((f) => {
                                  const cur = new Set(f.pending_materials || []);
                                  if (chk) cur.add(m.key); else cur.delete(m.key);
                                  return { ...f, pending_materials: Array.from(cur) };
                                })}
                                data-testid={`orders-pending-material-${m.key}-checkbox`}
                              />
                              <span className="text-sm text-slate-800">{m.label}</span>
                            </div>
                            <span className="text-[11px] font-mono tabular-nums text-slate-500">{count}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {filter.pending_only && masterList.length > 0 && pendingMaterialsAvailable.length === 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                    No pending materials in your current scope.
                  </div>
                )}

                {/* Wave 4.10e — Raw Material procurement status. Filters to
                    orders with a pending side-material requirement sitting in
                    the chosen buyer state (Order Pending / Order Raised /
                    Material Collected). Scoped to the materials selected above
                    when any are ticked, else all side materials the buyer can
                    see. Independent of the master pending toggle. */}
                {rmVisibleSideMaterialKeys.size > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100" data-testid="orders-rm-status-section">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                        Raw Material Status
                      </div>
                      {filter.rm_status !== 'all' && (
                        <button
                          type="button"
                          onClick={() => setFilter((f) => ({ ...f, rm_status: 'all' }))}
                          className="text-[11px] text-slate-500 hover:text-slate-800 underline"
                          data-testid="orders-rm-status-clear"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">
                      Procurement state of the buyer's raw material.
                    </div>
                    <div className="grid grid-cols-1 gap-1.5" role="radiogroup" aria-label="Raw material status">
                      {[{ key: 'all', label: 'Any status' }, ...RM_STATUS_ORDER.map((k) => ({ key: k, label: RM_STATUS_LABELS[k] }))].map((opt) => {
                        const active = (filter.rm_status || 'all') === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => setFilter((f) => ({ ...f, rm_status: opt.key }))}
                            className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-sm border transition-colors ${
                              active
                                ? 'bg-sky-50 border-sky-300 text-sky-900 font-medium'
                                : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                            }`}
                            data-testid={`orders-rm-status-${opt.key}`}
                          >
                            <span>{opt.label}</span>
                            {active && <CheckCircle2 size={14} className="text-sky-600" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>
                {/* Wave — Workflow — Reference No. filter (own inline popover).
                    Only rendered for companies that use reference numbers. */}
                {refEnabled && (
                  <ReferenceFilter
                    options={referenceOptions}
                    value={filter.reference_nos || []}
                    onChange={(next) => setFilter((f) => ({ ...f, reference_nos: next }))}
                    label={refLabel}
                    testIdPrefix="orders-reference-filter"
                  />
                )}
              </div>

              {/* Wave 3.70 — Clear all filters (endcap). Resets every filter to
                  its default state. Shown only when at least one filter is
                  non-default so it never adds noise on a fresh page load. */}
              {(() => {
                const isDefault = (
                  filter.status === 'active'
                  && !filter.goldsmith_id
                  && !filter.customer_id
                  && filter.stage === 'all'
                  && filter.substage === 'all'
                  && !filter.search
                  && !filter.pending_only
                  && (!filter.pending_materials || filter.pending_materials.length === 0)
                  && (!filter.rm_status || filter.rm_status === 'all')
                  && (!filter.reference_nos || filter.reference_nos.length === 0)
                  && (!filter.cad_source || filter.cad_source === 'all')
                  && !filter.jewellery_type
                  && !filter.date_from
                  && !filter.date_to
                );
                if (isDefault) return null;
                return (
                  <div className="lg:ml-auto lg:pl-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFilter({
                        status: 'active',
                        goldsmith_id: '',
                        customer_id: '',
                        stage: 'all',
                        substage: 'all',
                        search: '',
                        pending_only: false,
                        pending_materials: [],
                        rm_status: 'all',
                        reference_nos: [],
                        cad_source: 'all',
                        jewellery_type: '',
                        date_from: '',
                        date_to: '',
                      })}
                      className="h-9 text-slate-600 hover:text-[var(--ink-navy)] hover:bg-slate-100"
                      data-testid="orders-clear-filters"
                      title="Reset every filter to its default value"
                    >
                      <FilterX size={14} className="mr-1.5" />
                      Clear filters
                    </Button>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ── Band 3 — Active-filter chips ───────────────────────────── */}
          {/* Wave 3.73 — one removable chip per non-default filter so operators
              can drop them individually via the ✕ affordance. Hidden entirely
              when every filter is at default (FilterChipRow returns null). */}
          <FilterChipRow
            filter={filter}
            onChange={setFilter}
            goldsmiths={goldsmiths}
            customers={customers}
            referenceLabel={refLabel}
          />
        </div>
      </div>
      {/* ── Created-date range — separate compact row directly above the list.
          Filters the order universe by the ORDER FORM's creation date (IST
          calendar day). Runs server-side (see load()) so it stays accurate
          across the full paginated set. Either bound may be left empty for an
          open-ended range. ───────────────────────────────────────────────── */}
      <div
        className={`mb-3 flex flex-col gap-2 rounded-lg border px-3 py-2 shadow-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-2 ${
          (filter.date_from || filter.date_to)
            ? 'border-[var(--jewellery-icon)]/40 bg-[var(--jewellery-bg)]'
            : 'border-[var(--divider)] bg-white/90'
        }`}
        data-testid="orders-created-range-row"
      >
        <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-[var(--ink-navy)]">
          <CalendarRange size={15} className="text-[var(--jewellery-icon)]" />
          <span>Order created</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[150px]">
            <DatePicker
              value={filter.date_from}
              onValueChange={(v) => setFilter((f) => ({ ...f, date_from: v || '' }))}
              placeholder="From date"
              maxDate={filter.date_to || undefined}
              data-testid="orders-created-from"
            />
          </div>
          <span className="text-slate-400 text-sm" aria-hidden="true">→</span>
          <div className="w-[150px]">
            <DatePicker
              value={filter.date_to}
              onValueChange={(v) => setFilter((f) => ({ ...f, date_to: v || '' }))}
              placeholder="To date"
              minDate={filter.date_from || undefined}
              data-testid="orders-created-to"
            />
          </div>
          {(filter.date_from || filter.date_to) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilter((f) => ({ ...f, date_from: '', date_to: '' }))}
              className="h-9 px-2 text-slate-600 hover:text-[var(--ink-navy)] hover:bg-slate-100"
              data-testid="orders-created-range-clear"
              title="Clear the created-date range"
            >
              <XIcon size={14} className="mr-1" /> Clear
            </Button>
          )}
        </div>
        <p className="text-[11px] text-slate-500 basis-full sm:basis-auto sm:ml-1" data-testid="orders-created-range-hint">
          Filters by the order form's creation date. Set only <span className="font-medium">From</span> to see everything from that day onward, or only <span className="font-medium">To</span> for everything up to it.
        </p>
      </div>

      {canBulk && selectedIds.size > 0 && (
        <div
          className="sticky top-2 z-20 mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-[var(--ink-navy)] px-3 py-2 shadow-md"
          data-testid="orders-bulk-toolbar"
        >
          <span className="flex items-baseline gap-1.5 text-sm font-medium text-white" data-testid="orders-bulk-count">
            <span><span className="font-semibold tabular-nums">{selectedIds.size}</span> selected{selectionSpansPages ? ' across pages' : ''}</span>
            {selectionSpansPages && (
              <span className="text-[11px] font-normal text-white/70" data-testid="orders-bulk-count-context">
                ({selectedOnThisPageCount} on this page)
              </span>
            )}
          </span>
          <div className="mx-1 h-5 w-px bg-white/25" />
          {anyHoldable && (
            <Button size="sm" className="h-8 bg-white/10 text-white hover:bg-white/20 border-0" onClick={() => openBulk('hold')} data-testid="orders-bulk-hold">
              <Pause size={13} className="mr-1.5" /> Hold
            </Button>
          )}
          {anyOnHold && (
            <Button size="sm" className="h-8 bg-white/10 text-white hover:bg-white/20 border-0" onClick={() => openBulk('resume')} data-testid="orders-bulk-resume">
              <Play size={13} className="mr-1.5" /> Resume
            </Button>
          )}
          <Button size="sm" className="h-8 bg-white/10 text-white hover:bg-white/20 border-0" onClick={() => openBulk('cancel')} data-testid="orders-bulk-cancel">
            <XCircle size={13} className="mr-1.5" /> Cancel
          </Button>
          {designEnabled && (
            <Button size="sm" className="h-8 bg-white/10 text-white hover:bg-white/20 border-0" onClick={() => { setBulkDesignId(null); setBulkDesignOpen(true); }} data-testid="orders-bulk-assign-design">
              <Palette size={13} className="mr-1.5" /> Assign Design
            </Button>
          )}
          <span
            title={anyIssuedMaterial
              ? `${issuedSelectedCount} selected order${issuedSelectedCount === 1 ? ' has' : 's have'} raw material issued — reverse the issues before deleting.`
              : 'Permanently delete the selected orders'}
            data-testid="orders-bulk-delete-wrap"
          >
            <Button
              size="sm"
              className="h-8 bg-rose-600 text-white hover:bg-rose-700 border-0 disabled:opacity-50 disabled:hover:bg-rose-600"
              onClick={() => openBulk('delete')}
              disabled={anyIssuedMaterial}
              data-testid="orders-bulk-delete"
            >
              <Trash2 size={13} className="mr-1.5" /> Delete
            </Button>
          </span>
          {anyIssuedMaterial && (
            <span className="inline-flex items-center gap-1 text-[11px] text-rose-200" data-testid="orders-bulk-delete-blocked-note">
              <AlertTriangle size={12} /> {issuedSelectedCount} with issued material can’t be deleted
            </span>
          )}
          <button
            type="button"
            onClick={clearSelection}
            className="ml-auto inline-flex items-center gap-1 text-[12px] text-white/70 hover:text-white"
            title="Clear selection"
            data-testid="orders-bulk-close"
          >
            <XIcon size={15} /> Clear
          </button>
        </div>
      )}
      {/* Wave 4.x — Top pagination bar so operators can change page without
          scrolling to the bottom of a long list. */}
      <OrdersPaginationBar
        position="top"
        total={ordersTotal}
        rangeStart={pageStart + 1}
        rangeEnd={pageEnd}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        page={currentPage}
        totalPages={totalPages}
        goToPage={goToPage}
        pageJump={pageJump}
        setPageJump={setPageJump}
        totalKnown={totalKnown}
        loadingMore={!ordersFullyLoaded}
      />
      {(() => {
        // Wave 3.92 — Shared client-side substring filter used by both
        // Table and Grid views so the operator sees the same subset
        // regardless of view mode.
        //
        // Wave 3.99u — Also enforces the Pending Requirements +
        // Raw-material multi-select filters client-side, so they
        // stack on top of the server-side status/goldsmith/customer
        // filters without needing a backend contract change.
        //
        // Wave 3.99aa — Filter logic now lives in the `filteredOrders`
        // memo above; the count pill in the header uses the same
        // memo so they can never drift.
        const filtered = filteredOrders;
        const needle = (filter.search || '').trim().toLowerCase();
        const wantsPending = filter.pending_only || (filter.pending_materials || []).length > 0;
        // Wave 4.x — Progressive load: while we can't render an accurate page
        // yet (a client filter is active pre-full-load, or the operator paged
        // past the fast first batch), show a loading card instead of partial
        // or empty results.
        if (awaitingFullData) {
          return (
            <Card className="p-10 text-center text-slate-500" data-testid="orders-loading-full">
              <Loader2 size={22} className="mx-auto mb-3 animate-spin text-[var(--jewellery-icon)]" />
              Loading all orders…
            </Card>
          );
        }
        if (view === 'grid') {
          if (filtered.length === 0) {
            return (
              <Card className="p-10 text-center text-slate-500" data-testid="orders-grid-empty">
                {needle
                  ? `No orders match "${filter.search}"`
                  : (wantsPending ? 'No orders match the pending-requirements filter' : 'No orders yet')}
              </Card>
            );
          }
          return (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 grid-auto-wide"
              data-testid="orders-grid"
            >
              {pagedOrders.map((o) => (
                <div key={o.id} className="relative">
                  {canBulk && (
                    <div
                      className={`absolute left-2 top-2 z-10 rounded-md p-1 shadow-sm ${selectedIds.has(o.id) ? 'bg-[var(--ink-navy)]' : 'bg-white/95 border border-[var(--divider)]'}`}
                    >
                      <Checkbox
                        checked={selectedIds.has(o.id)}
                        onCheckedChange={() => toggleSelect(o.id)}
                        aria-label={`Select ${o.order_no}`}
                        data-testid={`orders-select-${o.id}`}
                        className={selectedIds.has(o.id) ? 'border-white data-[state=checked]:bg-white data-[state=checked]:text-[var(--ink-navy)]' : ''}
                      />
                    </div>
                  )}
                  <OrderCard
                    order={o}
                    canToggleSla={canToggleSla}
                    canEdit={canEditOrders}
                    onUpdateCompletion={onUpdateCompletion}
                    refLabel={refLabel}
                    refLabelShort={refLabelShort}
                    onOpenPdf={isSalesRole ? undefined : () => setPdfDialog({ open: true, orderId: o.id, orderNo: o.order_no })}
                    onOpenSla={() => setSlaToggle({ open: true, order: o })}
                  />
                </div>
              ))}
            </div>
          );
        }
        // Wave 4.x — "Select all visible" now means the CURRENT PAGE (the
        // only rows on screen). Selection persists across pages via selectedIds.
        const allVisibleSelected = canBulk && pagedOrders.length > 0 && pagedOrders.every((o) => selectedIds.has(o.id));
        const toggleSelectAllVisible = () => setSelectedIds((prev) => {
          const n = new Set(prev);
          if (pagedOrders.every((o) => n.has(o.id))) pagedOrders.forEach((o) => n.delete(o.id));
          else pagedOrders.forEach((o) => n.add(o.id));
          return n;
        });
        return (
      <Card className="overflow-hidden">
        <div className="ledger-table overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              {canBulk && (
                <th className="w-10 text-left">
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={toggleSelectAllVisible}
                    aria-label="Select all visible orders"
                    data-testid="orders-select-all"
                  />
                </th>
              )}
              <th className="text-left">Order</th>
              {refEnabled && <th className="text-left">{refLabelShort}</th>}
              <th className="text-left">Customer</th>
              <th className="text-left">Type</th>
              <th className="text-left">Goldsmith</th>
              <th className="text-left">Stage</th>
              <th className="text-left">Requirements</th>
              <th className="text-left">Est. Completion</th>
              <th className="text-left">Created</th>
              <th className="text-left">Days</th>
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody data-testid="orders-table-body">
              {(() => {
                if (filtered.length === 0) {
                  return (
                    <tr>
                      <td colSpan={(refEnabled ? 11 : 10) + (canBulk ? 1 : 0)} className="text-center py-10 text-slate-500">
                        {needle
                          ? `No orders match "${filter.search}"`
                          : (wantsPending ? 'No orders match the pending-requirements filter' : 'No orders yet')}
                      </td>
                    </tr>
                  );
                }
                return pagedOrders.map((o) => {
                const reqs = o.requirements || [];
                const total = reqs.length;
                const done = reqs.filter(r => r.status === 'complete').length;
                // Wave 1.2 (revised) — IST-aware day count so it ticks at
                // India midnight regardless of browser timezone.
                const daysLabel = o.created_at ? formatDaysSince(o.created_at) : null;
                // Wave 3.99h — Preview thumbnail parity with Grid view.
                const preview = pickOrderPreview(o);
                return (
                <tr key={o.id} data-testid={`order-row-${o.id}`} data-selected={selectedIds.has(o.id) ? 'true' : undefined} className={selectedIds.has(o.id) ? 'bg-[var(--jewellery-bg)]/40' : undefined}>
                  {canBulk && (
                    <td>
                      <Checkbox
                        checked={selectedIds.has(o.id)}
                        onCheckedChange={() => toggleSelect(o.id)}
                        aria-label={`Select ${o.order_no}`}
                        data-testid={`orders-row-select-${o.id}`}
                      />
                    </td>
                  )}
                  <td>
                    {/* Wave 3.99h — Grid ↔ Table parity: thumbnail with
                        HoverCard preview, Order No chip + Title stacked
                        in a single column so both views read alike. */}
                    <div className="flex items-start gap-2.5">
                      <OrderPreviewHover order={o}>
                        <Link
                          to={`/orders/${o.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 block w-11 h-11 rounded-md overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 border border-[var(--divider)] hover:border-[var(--gold-accent)] transition-colors"
                          title={`Preview ${o.order_no}`}
                          data-testid={`order-row-thumb-${o.id}`}
                        >
                          {preview.kind === 'image' && preview.src ? (
                            <img
                              src={preview.src}
                              alt={o.title || o.order_no}
                              className="w-full h-full object-contain"
                              loading="lazy"
                            />
                          ) : preview.kind === 'pdf' ? (
                            <div className="w-full h-full flex items-center justify-center text-rose-500">
                              <FileText size={16} strokeWidth={1.4} />
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <FileText size={16} strokeWidth={1.2} />
                            </div>
                          )}
                        </Link>
                      </OrderPreviewHover>
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/orders/${o.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block font-mono text-[12px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded hover:underline hover:bg-[var(--jewellery-bg)]/80 transition-colors"
                          data-testid={`order-no-link-${o.id}`}
                        >
                          {o.order_no}
                        </Link>
                        {o.design_no && (
                          <DesignNoBadge designNo={o.design_no} className="ml-1.5 align-middle" testId={`order-row-design-${o.id}`} />
                        )}
                        {o.title && (
                          <div className="text-[12px] text-slate-700 mt-0.5 truncate" title={o.title}>
                            {o.title}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  {refEnabled && (
                    <td data-testid={`order-row-reference-${o.id}`}>
                      {o.reference_no != null ? (
                        <ReferenceChip
                          referenceNo={o.reference_no}
                          className="inline-flex items-center rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-[12px] font-mono tabular-nums"
                          testId={`order-row-reference-chip-${o.id}`}
                        >
                          {o.reference_no}
                        </ReferenceChip>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  )}
                  <td className="text-sm text-slate-700" data-testid={`order-row-customer-${o.id}`}>
                    {o.customer_name ? (
                      <span className="font-medium">{o.customer_name}</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="text-xs text-slate-700" data-testid={`order-row-type-${o.id}`}>
                    {o.jewellery_type ? (
                      <Badge variant="outline" className="bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] border-[var(--jewellery-icon)]/20 text-[10px] font-medium">
                        {o.jewellery_type}
                      </Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="text-sm">{o.goldsmith_name}</td>
                  <td>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StageBadge
                        stage={o.stage}
                        substage={o.substage}
                        holdStatus={o.hold_status}
                        activeSteps={o.active_steps}
                      />
                      <SlaScopeBadge scope={o.sla_scope} />
                      {o.in_house_cad === false && (
                        <Badge
                          className="bg-pink-500 text-white border-none text-[10px] font-semibold"
                          data-testid={`order-row-external-cad-${o.id}`}
                          title="CAD developed externally"
                        >
                          EXTERNAL CAD
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="text-xs text-slate-600">{total > 0 ? `${done}/${total} done` : <span className="text-slate-400">—</span>}</td>
                  <td className="text-xs text-slate-500">{o.estimated_completion ? formatDate(o.estimated_completion) : '—'}</td>
                  <td className="text-xs text-slate-500" data-testid={`order-row-created-${o.id}`}>{formatDate(o.created_at)}</td>
                  <td className="text-xs font-mono text-slate-700" data-testid={`order-days-${o.id}`}>{daysLabel || '—'}</td>
                  <td className="text-right">
                    {/* Wave 3.21 — Consolidated row Actions dropdown. Replaces
                        the single Open link and provides a place to add more
                        per-row actions (e.g. Print Order Form PDF). */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          data-testid={`order-actions-trigger-${o.id}`}
                        >
                          <MoreHorizontal size={14} />
                          <span className="sr-only">Open order actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem
                          onSelect={(e) => { e.preventDefault(); window.open(`/orders/${o.id}`, '_blank', 'noopener,noreferrer'); }}
                          data-testid={`order-row-action-open-${o.id}`}
                          className="cursor-pointer"
                        >
                          <ExternalLink size={14} className="mr-2 text-[var(--ink-navy)]" />
                          Open Order
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {!isSalesRole && (
                        <DropdownMenuItem
                          onSelect={(e) => { e.preventDefault(); setPdfDialog({ open: true, orderId: o.id, orderNo: o.order_no }); }}
                          data-testid={`order-row-action-print-${o.id}`}
                          className="cursor-pointer text-blue-700 focus:text-blue-700"
                        >
                          <FileText size={14} className="mr-2" />
                          Order Form PDF
                        </DropdownMenuItem>
                        )}
                        {canToggleSla && (o.stage !== 'Completed' && o.stage !== 'Cancelled') && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); setSlaToggle({ open: true, order: o }); }}
                              data-testid={`order-row-action-sla-toggle-${o.id}`}
                              className="cursor-pointer"
                            >
                              <Bell size={14} className="mr-2 text-[var(--gold-accent)]" />
                              SLA Tracking…
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
                );
              });
              })()}
            </tbody>
          </table>
        </div>
      </Card>
        );
      })()}

      {/* Wave 4.x — Bottom pagination bar (mirror of the top one). */}
      <OrdersPaginationBar
        position="bottom"
        total={ordersTotal}
        rangeStart={pageStart + 1}
        rangeEnd={pageEnd}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        page={currentPage}
        totalPages={totalPages}
        goToPage={goToPage}
        pageJump={pageJump}
        setPageJump={setPageJump}
        totalKnown={totalKnown}
        loadingMore={!ordersFullyLoaded}
      />

      {/* Wave 4.x — Bulk action confirmation (CA/SA). Danger styling for
          Cancel/Delete; optional shared reason for Hold. */}
      <Dialog open={!!bulkAction} onOpenChange={(v) => { if (!v && !bulkBusy) setBulkAction(null); }}>
        <DialogContent className="max-w-md" data-testid="orders-bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className={bulkAction && BULK_META[bulkAction].danger ? 'text-rose-600' : undefined}>
              {bulkAction ? BULK_META[bulkAction].title : ''}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === 'delete'
                ? `Permanently delete ${selectedIds.size} order${selectedIds.size === 1 ? '' : 's'}? This can't be undone. Orders with issued material will be skipped.`
                : bulkAction === 'cancel'
                ? `Cancel ${selectedIds.size} order${selectedIds.size === 1 ? '' : 's'}? Cancelled orders stop production.`
                : bulkAction === 'resume'
                ? `Resume ${selectedIds.size} order${selectedIds.size === 1 ? '' : 's'} currently on hold.`
                : `Put ${selectedIds.size} order${selectedIds.size === 1 ? '' : 's'} on hold with one shared reason.`}
            </DialogDescription>
          </DialogHeader>
          {bulkAction && BULK_META[bulkAction].needsReason && (
            <div className="space-y-1.5">
              <Label htmlFor="orders-bulk-reason">Reason (optional)</Label>
              <Textarea
                id="orders-bulk-reason"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="Why are these orders going on hold?"
                data-testid="orders-bulk-reason"
              />
            </div>
          )}
          {bulkAction === 'delete' && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700" data-testid="orders-bulk-delete-warning">
              This permanently removes the selected order forms along with their comments and stage history.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAction(null)} disabled={bulkBusy} data-testid="orders-bulk-back-btn">
              Back
            </Button>
            <Button
              onClick={runBulk}
              disabled={bulkBusy}
              className={bulkAction && BULK_META[bulkAction].danger ? 'bg-rose-600 hover:bg-rose-700 text-white' : undefined}
              data-testid="orders-bulk-confirm-btn"
            >
              {bulkBusy && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              {bulkAction === 'delete' ? 'Delete permanently' : bulkAction === 'cancel' ? 'Cancel orders' : bulkAction === 'resume' ? 'Resume orders' : 'Put on hold'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Design — link many selected orders to one design at once. */}
      <Dialog open={bulkDesignOpen} onOpenChange={(v) => { if (!bulkDesignBusy) setBulkDesignOpen(v); }}>
        <DialogContent className="max-w-lg" data-testid="orders-bulk-design-dialog">
          <DialogHeader>
            <DialogTitle>Assign design to {selectedIds.size} order{selectedIds.size === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>Pick a design number to link to every selected order. No image is uploaded or duplicated — the orders simply reference the design.</DialogDescription>
          </DialogHeader>
          <DesignPicker companyId={companyId} value={bulkDesignId} onChange={(id) => setBulkDesignId(id)} testIdPrefix="orders-bulk-design-picker" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDesignOpen(false)} disabled={bulkDesignBusy}>Cancel</Button>
            <Button className="luxury-button" onClick={runBulkAssignDesign} disabled={!bulkDesignId || bulkDesignBusy} data-testid="orders-bulk-design-confirm">
              {bulkDesignBusy && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              Assign Design
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Order Form</DialogTitle><DialogDescription>Issued to a goldsmith for manufacturing.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Goldsmith</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    items={goldsmiths}
                    value={form.goldsmith_id}
                    onValueChange={(v) => setForm({ ...form, goldsmith_id: v || '', existing_reference_no: null })}
                    itemToLabel={(g) => g.name}
                    itemToSubLabel={(g) => [g.phone, g.email].filter(Boolean).join(' · ') || null}
                    searchKeys={['name', 'phone', 'email']}
                    placeholder={goldsmiths.length === 0 ? 'No goldsmiths yet — click + to create' : 'Select goldsmith'}
                    searchPlaceholder="Search goldsmith…"
                    disabled={goldsmiths.length === 0}
                    testId="order-goldsmith-select"
                  />
                </div>
                <Button type="button" variant="outline" size="icon" onClick={() => setGoldsmithDlgOpen(true)} data-testid="order-goldsmith-create-button" title="Create goldsmith"><Plus size={14} /></Button>
              </div>
            </div>
            {/* Wave 3.46 — Customer (optional). Searchable combobox with
                inline "+ Add new customer" affordance. */}
            <div>
              <Label>Customer <span className="text-slate-400">(optional)</span></Label>
              <CustomerCombobox
                customers={customers}
                value={form.customer_id}
                onChange={(id) => setForm({ ...form, customer_id: id || '', existing_reference_no: null })}
                companyId={companyId}
                onCustomerCreated={loadCustomers}
                placeholder="Search or add a customer…"
                testIdPrefix="order-customer"
              />
              <div className="text-[10px] text-slate-500 mt-1">
                Surfaces on the Orders list, Order Detail header, and the printed Order Form PDF.
              </div>
            </div>
            <div>
              <Label>Type of Jewellery <span className="text-slate-400">(optional)</span></Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  {(() => {
                    // Wave 3.78 — Bugfix: the combobox previously used
                    // ``t.id`` (UUID) as the SearchableSelect value but
                    // stored ``t.name`` on the form. On next render
                    // ``value`` was the name and no item had that
                    // ``id`` → selected=null → placeholder shown even
                    // though the API call succeeded. We now use the
                    // ``name`` as both the id and value throughout so
                    // the button always reflects the picked entry.
                    const items = [
                      { id: '__none', name: '— No type —' },
                      ...jewelleryTypes.map((t) => ({ id: t.name, name: t.name })),
                    ];
                    return (
                      <SearchableSelect
                        items={items}
                        value={form.jewellery_type || '__none'}
                        onValueChange={(v) => setForm({ ...form, jewellery_type: !v || v === '__none' ? '' : v })}
                        itemToLabel={(t) => t.name}
                        searchKeys={['name']}
                        placeholder={jewelleryTypes.length ? 'Select type (e.g. Necklace, Ring)' : 'Loading types…'}
                        searchPlaceholder="Search type…"
                        testId="order-jewellery-type-select"
                      />
                    );
                  })()}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setTypeDlgOpen(true)}
                  data-testid="order-jewellery-type-add-button"
                  title="Add a new jewellery type"
                >
                  <Plus size={14} />
                </Button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Shows up on the Orders list, the resulting Jewellery card and helps you search.</div>
            </div>
            <div>
              <Label>Jewellery Gold Purity <span className="text-rose-600">*</span></Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    items={purities}
                    value={form.jewellery_purity_id}
                    onValueChange={(v) => setForm({ ...form, jewellery_purity_id: v || '', existing_reference_no: null })}
                    itemToLabel={(p) => `${p.name} (${(p.percent || 0).toFixed(2)}%)`}
                    searchKeys={['name']}
                    placeholder={purities.length ? 'Select purity (e.g., 18Kt)' : 'No purities — click + to create'}
                    searchPlaceholder="Search purity…"
                    disabled={purities.length === 0}
                    testId="order-purity-select"
                  />
                </div>
                <Button type="button" variant="outline" size="icon" onClick={() => setPurityDlgOpen(true)} data-testid="order-purity-create-button" title="Create purity"><Plus size={14} /></Button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Used to display Gold 999 → Jewellery purity conversion in the order ledger.</div>
            </div>
            {/* Wave 4.x — In-House CAD toggle. Ticked (default): CAD is made
                in our workshop; production records CAD/CAM/Chamka weights and
                the order appears on the CAD Development report. Unticked: CAD
                is external → CAD/CAM/Chamka are skipped and Ghat is the first
                active step. */}
            <div className="rounded-lg border border-[var(--divider)] bg-slate-50/70 p-3" data-testid="order-in-house-cad-section">
              <label className="flex items-start gap-2.5 cursor-pointer" data-testid="order-in-house-cad-label">
                <Checkbox
                  checked={!!form.in_house_cad}
                  onCheckedChange={(chk) => setForm({ ...form, in_house_cad: !!chk })}
                  className="mt-0.5"
                  data-testid="order-in-house-cad-toggle"
                />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-slate-800 flex items-center gap-1.5">
                    <Cpu size={13} className="text-indigo-600" /> In-House CAD
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    CAD is developed in our workshop — the production team records CAD, CAM &amp; Chamka weights and this order appears on the CAD Development report.
                    Untick if the CAD is made externally: CAD/CAM/Chamka are skipped and the order moves straight to <b>Ghat</b>.
                  </div>
                </div>
              </label>
            </div>
            {/* Reference-Number system — read-only auto number + optional
                "belongs to an existing" linker. A number can be shared ONLY
                across orders matching on Customer + Goldsmith + Gold Purity,
                so the linker unlocks only once all three are chosen above. */}
            {refEnabled && (() => {
              const canLinkExisting = !!(form.goldsmith_id && form.customer_id && form.jewellery_purity_id);
              const termLc = refLabelShort.toLowerCase();
              return (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3" data-testid="order-reference-section">
                <div className="flex items-center justify-between gap-2">
                  <Label className="flex items-center gap-1.5 text-indigo-900">
                    <Hash size={14} /> {refLabel}
                  </Label>
                  <span
                    className="inline-flex items-center font-mono text-[13px] bg-white text-indigo-700 border border-indigo-200 px-2.5 py-0.5 rounded tabular-nums"
                    data-testid="order-reference-preview"
                  >
                    {form.link_existing_reference && form.existing_reference_no
                      ? form.existing_reference_no
                      : (refNextPreview ?? '—')}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  {form.link_existing_reference && form.existing_reference_no
                    ? `This order will be grouped under the selected existing ${termLc}.`
                    : `Auto-assigned on save (next: ${refNextPreview ?? '—'}). Read-only.`}
                </div>
                <label
                  className={`flex items-start gap-2 mt-2.5 ${canLinkExisting ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                  data-testid="order-reference-link-label"
                >
                  <Checkbox
                    checked={!!form.link_existing_reference}
                    disabled={!canLinkExisting}
                    onCheckedChange={(chk) => setForm((f) => ({
                      ...f,
                      link_existing_reference: !!chk,
                      existing_reference_no: chk ? f.existing_reference_no : null,
                    }))}
                    className="mt-0.5"
                    data-testid="order-reference-link-toggle"
                  />
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-slate-800">
                      Belongs to an existing {termLc}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Group this item into an existing job/set (e.g. a necklace set) instead of starting a new {termLc}.
                    </div>
                  </div>
                </label>
                {!canLinkExisting && (
                  <div className="mt-2.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2" data-testid="order-reference-need-scope">
                    Select a goldsmith, customer and gold purity above to link this order to an existing {termLc}.
                  </div>
                )}
                {canLinkExisting && form.link_existing_reference && (
                  <div className="mt-2.5">
                    <ReferenceCombobox
                      companyId={companyId}
                      customerId={form.customer_id}
                      goldsmithId={form.goldsmith_id}
                      purityId={form.jewellery_purity_id}
                      value={form.existing_reference_no}
                      onChange={(rn) => setForm((f) => ({ ...f, existing_reference_no: rn }))}
                      label={refLabel}
                      testId="order-reference-combobox"
                    />
                  </div>
                )}
              </div>
              );
            })()}
            {/* Design Master — mandatory Design Number when the company has
                the feature enabled. Pick an existing design or create a new
                one from an uploaded image (which becomes the Master image). */}
            {designEnabled && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3" data-testid="order-design-section">
                <div className="flex items-center justify-between gap-2">
                  <Label className="flex items-center gap-1.5 text-indigo-900">
                    <Palette size={14} /> Design Number <span className="text-rose-600">*</span>
                  </Label>
                  <span
                    className="inline-flex items-center font-mono text-[13px] bg-white text-indigo-700 border border-indigo-200 px-2.5 py-0.5 rounded tabular-nums"
                    data-testid="order-design-selected"
                  >
                    {form.design_mode === 'new'
                      ? (form.design_new_image ? (designNextPreview || 'New') : '—')
                      : (form.design_id ? (form.design_selected_no || 'Selected') : '—')}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  Required for this company. Pick an existing design or create a new one from an image.
                </div>
                <div className="mt-2.5 inline-flex rounded-md border border-indigo-200 bg-white overflow-hidden" role="tablist">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, design_mode: 'existing' }))}
                    className={`px-3 py-1 text-[12px] transition-colors duration-150 ${form.design_mode === 'existing' ? 'bg-indigo-600 text-white' : 'text-indigo-700 hover:bg-indigo-50'}`}
                    data-testid="order-design-mode-existing"
                  >
                    Existing design
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, design_mode: 'new' }))}
                    className={`px-3 py-1 text-[12px] transition-colors duration-150 ${form.design_mode === 'new' ? 'bg-indigo-600 text-white' : 'text-indigo-700 hover:bg-indigo-50'}`}
                    data-testid="order-design-mode-new"
                  >
                    New design
                  </button>
                </div>
                {form.design_mode === 'existing' ? (
                  <div className="mt-2.5">
                    <DesignPicker
                      companyId={companyId}
                      value={form.design_id}
                      onChange={(id, obj) => setForm((f) => ({ ...f, design_id: id, design_selected_no: obj?.design_no || '' }))}
                      testIdPrefix="order-design-picker"
                    />
                  </div>
                ) : (
                  <div className="mt-2.5">
                    <input ref={designFileRef} type="file" accept="image/*" hidden onChange={onDesignFile} data-testid="order-design-file-input" />
                    {form.design_new_image ? (
                      <div className="relative inline-block border border-[var(--divider)] rounded-md overflow-hidden bg-white">
                        <img src={form.design_new_image} alt="New design" className="max-h-40 object-contain" data-testid="order-design-new-preview" />
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, design_new_image: '' }))}
                          className="absolute top-1 right-1 bg-white/90 text-rose-600 p-0.5 rounded"
                          data-testid="order-design-new-clear"
                          title="Remove"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ) : (
                      <Button type="button" variant="outline" size="sm" onClick={() => designFileRef.current?.click()} data-testid="order-design-upload-button">
                        <Upload size={14} className="mr-1.5" /> Upload design image
                      </Button>
                    )}
                    <div className="text-[10px] text-slate-500 mt-1">
                      This image becomes the Master image ({designNextPreview || 'RD-101'}) and shows on the order with a &quot;Linked to Design Master&quot; badge.
                    </div>
                  </div>
                )}
              </div>
            )}
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Diamond Necklace - Customer X" data-testid="order-title-input" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Estimated Completion</Label><DatePicker value={form.estimated_completion} onChange={(e) => setForm({ ...form, estimated_completion: e.target.value })} /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            {/* Wave 3.78/3.82/3.83/3.85 — Compact SLA summary + reuse of the
                Edit Product SLA dialog (draft mode) for full configuration. */}
            <NewOrderSlaSummary
              form={form}
              template={slaTemplateResolved}
              slaCustomized={slaCustomized}
              onOpenDialog={() => setSlaDraftOpen(true)}
            />
            <div>
              <Label className="text-rose-700 font-semibold">
                Special Comments
                <span className="text-[10px] font-normal text-rose-600 ml-1.5">(printed in bold red on the order form)</span>
              </Label>
              <Textarea
                value={form.special_comments}
                onChange={(e) => setForm({ ...form, special_comments: e.target.value })}
                placeholder="e.g. Use Bombay polish · Match to attached photo · Handle stones with care"
                className="border-rose-300 focus-visible:ring-rose-500"
                rows={2}
                data-testid="order-special-comments-input"
              />
            </div>
            <div>
              <Label>Jewellery Images (optional, you can add more later)</Label>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="order-create-images-input" />
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="order-create-upload-button">
                  <Upload size={14} className="mr-1.5" /> Add Images
                </Button>
              </div>
              {form.images.length > 0 && (
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                  {form.images.map((src, i) => (
                    <div key={i} className="relative border border-[var(--divider)] rounded-md overflow-hidden bg-white flex flex-col">
                      <div className="relative w-full aspect-square">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute top-1 right-1 bg-white/90 text-rose-600 p-0.5 rounded"
                          data-testid={`order-create-remove-img-${i}`}
                          title="Remove"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <div className="px-2 py-1 border-t border-[var(--divider)]">
                        <Input
                          value={(form.image_titles || [])[i] || ''}
                          onChange={(e) => setImageTitle(i, e.target.value)}
                          placeholder="Add a title…"
                          className="h-7 text-[11px] px-1.5"
                          data-testid={`order-create-image-title-${i}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-1">PNG/JPG up to 2MB each. Add an optional title — you can rename any time later on the order page.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => create(true)} disabled={!form.goldsmith_id || !form.title || !form.jewellery_purity_id || (designEnabled && (form.design_mode === 'new' ? !form.design_new_image : !form.design_id))} data-testid="order-submit-and-print-button">
              <FileText size={14} className="mr-1.5" /> Save & Print
            </Button>
            <Button onClick={() => create(false)} className="luxury-button" disabled={!form.goldsmith_id || !form.title || !form.jewellery_purity_id || (designEnabled && (form.design_mode === 'new' ? !form.design_new_image : !form.design_id))} data-testid="order-submit-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GoldsmithFormDialog
        open={goldsmithDlgOpen}
        onOpenChange={setGoldsmithDlgOpen}
        companyId={companyId}
        onCreated={async (g) => { await loadGs(); setForm((p) => ({ ...p, goldsmith_id: g.id })); }}
      />
      <PurityFormDialog
        open={purityDlgOpen}
        onOpenChange={setPurityDlgOpen}
        companyId={companyId}
        onCreated={async (p) => { await loadPurities(); setForm((f) => ({ ...f, jewellery_purity_id: p.id })); }}
      />
      {/* Wave 3.25 — Inline "Add new Type of Jewellery" dialog. Created
          types are scoped to the active company and auto-selected on
          the Order Form. */}
      <JewelleryTypeAddDialog
        open={typeDlgOpen}
        onOpenChange={setTypeDlgOpen}
        companyId={companyId}
        onCreated={async (t) => {
          await loadJewelleryTypes();
          setForm((f) => ({ ...f, jewellery_type: t?.name || f.jewellery_type }));
        }}
      />
      {pdfDialog.orderId && (
        <PdfDownloadDialog
          open={pdfDialog.open}
          onOpenChange={(o) => setPdfDialog({ ...pdfDialog, open: o })}
          title={`Print Order Form ${pdfDialog.orderNo || ''}`}
          description="Choose whether to print the company logo and name on this order form."
          companyId={companyId}
          endpoint={`/orders/${pdfDialog.orderId}/pdf`}
          filename={`OrderForm_${(pdfDialog.orderNo || pdfDialog.orderId).replace(/\//g, '-')}.pdf`}
          mode="print"
        />
      )}
      {/* Wave 3.83 — Per-order SLA tracking toggle dialog (CA/SA only) */}
      <SlaTrackingToggleDialog
        open={slaToggle.open}
        order={slaToggle.order}
        onOpenChange={(o) => setSlaToggle((s) => ({ ...s, open: o }))}
        onSaved={load}
      />

      {/* Wave 3.85 — Draft-mode SLA dialog for the New Order form.
          Reuses the same EditOrderSlaDialog used on Order Detail so operators
          get a single, familiar SLA configuration surface. */}
      <EditOrderSlaDialog
        open={slaDraftOpen}
        onOpenChange={setSlaDraftOpen}
        companyId={companyId}
        mode="draft"
        jewelleryType={form.jewellery_type}
        draftValues={{
          sla_per_stage: form.sla_per_stage,
          delay_notify_by_stage: form.delay_notify_by_stage,
          delay_notify_user_ids: form.delay_notify_user_ids,
          procurement_config: form.procurement_config,
          sla_scope: form.sla_scope,
          priority: form.priority,
          order_raised_notify_user_ids: form.order_raised_notify_user_ids,
        }}
        onDraftSave={(payload) => {
          setForm((p) => ({
            ...p,
            sla_per_stage: payload.sla_per_stage || {},
            delay_notify_by_stage: payload.delay_notify_by_stage || {},
            delay_notify_user_ids: payload.delay_notify_user_ids || [],
            procurement_config: payload.procurement_config || p.procurement_config,
            sla_scope: payload.sla_scope || p.sla_scope,
            priority: payload.priority || p.priority,
            order_raised_notify_user_ids: payload.order_raised_notify_user_ids || [],
          }));
          setSlaCustomized(true);
        }}
      />
    </div>
  );
}



// ============================================================================
// Wave 3.92 — Order Grid Card
// ============================================================================
// Image-first card used by the Orders → Grid view. Each card shows a
// preview image at the top, then the key order metadata below.
//
// Preview image resolution order:
//   1. images_thumb[0] — a rendered JPEG that the backend generates for
//      BOTH raw images AND the first page of PDF attachments (via
//      PyMuPDF/fitz). This is the preferred source: small (fast to
//      paint) and always in an <img>-friendly format.
//   2. images[0]  — the full-res attachment. Used only when it's an
//      image data URI. A ``data:application/pdf`` URI cannot render in
//      an <img> tag, so we fall through to the placeholder.
//   3. Placeholder — a soft-tone icon block (FileText for PDF-only
//      orders, or the generic order glyph for imageless orders).
// ============================================================================
function pickOrderPreview(order) {
  const imgs = Array.isArray(order?.images) ? order.images : [];
  const thumbs = Array.isArray(order?.images_thumb) ? order.images_thumb : [];
  // Wave 3.98 — Prefer the full-res image data URI on the Grid card so
  // jewellery photos paint sharply at the card's canvas (~250×188 px)
  // instead of being upscaled from a tiny backend thumbnail. Fall back
  // to ``images_thumb`` only for PDF attachments — the backend renders
  // a JPEG of the PDF's first page into ``images_thumb`` (browsers
  // cannot render a ``data:application/pdf`` URI inside an <img> tag).
  for (let i = 0; i < imgs.length; i += 1) {
    const u = imgs[i];
    if (typeof u === 'string' && u.startsWith('data:image/')) {
      return { src: u, kind: 'image' };
    }
  }
  // Fallback: backend-rendered thumbnail (used for PDFs and any legacy
  // orders that never captured the raw image data URI).
  for (let i = 0; i < imgs.length; i += 1) {
    const t = thumbs[i];
    if (typeof t === 'string' && t.startsWith('data:image/')) {
      return { src: t, kind: 'image' };
    }
  }
  // PDF-only order (or an order that has PDFs whose first pages
  // couldn't be rendered) — surface a PDF badge placeholder.
  const hasPdf = imgs.some((u) => typeof u === 'string' && u.startsWith('data:application/pdf'));
  // Wave 3.99i — Consult server-side hints so orders with attachments
  // but no renderable thumbnail still show a meaningful placeholder
  // (was silently falling through to "No Image").
  const kinds = Array.isArray(order?.image_kinds) ? order.image_kinds : [];
  const count = Number(order?.image_count || 0);
  const kindHasPdf = hasPdf || (order?.has_pdf === true) || kinds.includes('pdf');
  const kindHasImage = kinds.includes('image');
  if (kindHasPdf) return { src: null, kind: 'pdf', count };
  if (count > 0 && kindHasImage) return { src: null, kind: 'image_missing', count };
  if (count > 0) return { src: null, kind: 'unknown', count };
  return { src: null, kind: 'none' };
}

// ============================================================================
// Wave 3.99h — Shared order preview thumbnail + hover popover so the
// Grid and Table views paint the same rich preview experience. Grid
// consumers pass ``variant="grid"`` to skip the wrapper and use their
// own trigger; Table consumers pass ``variant="thumb"`` to render a
// square thumb the operator can hover over.
// ============================================================================
function OrderPreviewHover({ order, children }) {
  const hoverAttachments = React.useMemo(() => collectHoverAttachments(order), [order]);
  const [hoverIdx, setHoverIdx] = React.useState(0);
  const activeHover = hoverAttachments[hoverIdx] || hoverAttachments[0] || null;
  const total = (order.requirements || []).length;
  const done = (order.requirements || []).filter((r) => r.status === 'complete').length;
  return (
    <HoverCard openDelay={200} closeDelay={80}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        className="w-[320px] p-3"
        data-testid={`order-hover-content-${order.id}`}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <div
              className="text-[13px] font-medium text-[var(--ink-navy)] truncate"
              title={order.title || order.order_no}
            >
              {order.title || order.order_no}
            </div>
            <span className="font-mono text-[10px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-1.5 py-0.5 rounded shrink-0">
              {order.order_no}
            </span>
          </div>
          {activeHover ? (
            <div className="rounded-md overflow-hidden bg-slate-100 border border-[var(--divider)]">
              {activeHover.src ? (
                <img
                  src={activeHover.src}
                  alt={activeHover.title || order.title || order.order_no}
                  className="w-full max-h-[280px] object-contain bg-slate-900/5"
                />
              ) : (
                <div className="w-full h-[200px] flex flex-col items-center justify-center text-slate-500 bg-gradient-to-br from-slate-50 to-slate-100">
                  <FileText size={44} strokeWidth={1.2} />
                  <div className="text-[11px] mt-1 uppercase tracking-wider">PDF · preview unavailable</div>
                </div>
              )}
              {(activeHover.title || activeHover.isPdf) && (
                <div className="px-2 py-1 flex items-center justify-between gap-2 bg-white border-t border-[var(--divider)]">
                  <div className="text-[11px] text-slate-600 truncate" title={activeHover.title}>
                    {activeHover.title || (activeHover.isPdf ? 'PDF attachment' : 'Image attachment')}
                  </div>
                  {activeHover.isPdf && (
                    <Badge variant="outline" className="text-[9px] border-rose-200 bg-rose-50 text-rose-700 shrink-0">
                      PDF
                    </Badge>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-[160px] rounded-md flex flex-col items-center justify-center text-slate-400 bg-gradient-to-br from-slate-50 to-slate-100 border border-[var(--divider)]">
              <FileText size={36} strokeWidth={1.2} />
              <div className="text-[11px] mt-1">No attachments</div>
            </div>
          )}
          {hoverAttachments.length > 1 && (
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              {hoverAttachments.map((att, idx) => (
                <button
                  type="button"
                  key={att.index}
                  onMouseEnter={() => setHoverIdx(idx)}
                  onFocus={() => setHoverIdx(idx)}
                  onClick={(e) => { e.preventDefault(); setHoverIdx(idx); }}
                  className={`shrink-0 relative w-12 h-12 rounded overflow-hidden border transition-all ${
                    idx === hoverIdx
                      ? 'border-[var(--gold-accent)] ring-1 ring-[var(--gold-accent)]'
                      : 'border-[var(--divider)] hover:border-slate-400'
                  }`}
                  aria-label={`Preview attachment ${idx + 1}`}
                >
                  {att.src ? (
                    <img src={att.src} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400">
                      <FileText size={14} />
                    </div>
                  )}
                  {att.isPdf && (
                    <span className="absolute bottom-0 right-0 text-[7px] px-1 bg-rose-600 text-white font-bold">
                      PDF
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-slate-500 pt-1 border-t border-[var(--divider)]">
            {order.customer_name && <div className="truncate"><span className="text-slate-400">Customer · </span>{order.customer_name}</div>}
            <div className="truncate"><span className="text-slate-400">Goldsmith · </span>{order.goldsmith_name || '—'}</div>
            {order.jewellery_type && <div className="truncate"><span className="text-slate-400">Type · </span>{order.jewellery_type}</div>}
            {total > 0 && <div className="truncate font-mono tabular-nums"><span className="text-slate-400">Reqs · </span>{done}/{total}</div>}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

// ============================================================================
// Wave 3.92 — Card Hover Preview
// ============================================================================
// Returns a list of {src, isPdf, title} for every attachment on the order,
// ready to be rendered inside the hover popover. We prefer the raw image
// data-URI for viewing (larger, higher quality) and fall back to the
// backend-rendered thumbnail for PDFs. Attachments that we can't render
// in any way (e.g. PDF without a rendered first page) are still surfaced
// as a labelled PDF chip so operators know they exist without opening
// the order.
// ============================================================================
function collectHoverAttachments(order) {
  const imgs = Array.isArray(order?.images) ? order.images : [];
  const thumbs = Array.isArray(order?.images_thumb) ? order.images_thumb : [];
  const titles = Array.isArray(order?.image_titles) ? order.image_titles : [];
  const out = [];
  for (let i = 0; i < imgs.length; i += 1) {
    const raw = imgs[i];
    const thumb = thumbs[i];
    const title = titles[i] || '';
    if (typeof raw === 'string' && raw.startsWith('data:image/')) {
      out.push({ src: raw, isPdf: false, title, index: i });
    } else if (typeof raw === 'string' && raw.startsWith('data:application/pdf')) {
      out.push({
        src: (typeof thumb === 'string' && thumb.startsWith('data:image/')) ? thumb : null,
        isPdf: true,
        title,
        index: i,
      });
    }
  }
  return out;
}

export function OrderCard({ order: o, canToggleSla, canEdit = false, onUpdateCompletion, onOpenPdf, onOpenSla, refLabel = 'Page No.', refLabelShort = 'Page' }) {
  const reqs = o.requirements || [];
  const total = reqs.length;
  const done = reqs.filter((r) => r.status === 'complete').length;
  const daysLabel = o.created_at ? formatDaysSince(o.created_at) : null;
  // Wave 4.x — Inline Estimated Completion editor state (popover).
  const [etaOpen, setEtaOpen] = React.useState(false);
  const [etaDraft, setEtaDraft] = React.useState(o.estimated_completion || '');
  const [etaSaving, setEtaSaving] = React.useState(false);
  React.useEffect(() => { setEtaDraft(o.estimated_completion || ''); }, [o.estimated_completion]);
  const saveEta = async (val) => {
    setEtaSaving(true);
    try {
      await onUpdateCompletion?.(o.id, val);
      setEtaOpen(false);
    } finally {
      setEtaSaving(false);
    }
  };
  // Estimated Delivery date change history (who changed it, when, old -> new).
  const [etaHistOpen, setEtaHistOpen] = React.useState(false);
  const [etaHistLoading, setEtaHistLoading] = React.useState(false);
  const [etaHistEvents, setEtaHistEvents] = React.useState(null);
  const loadEtaHistory = async () => {
    setEtaHistLoading(true);
    try {
      const { data } = await api.get(`/orders/${o.id}/estimated-delivery-history`);
      setEtaHistEvents(data?.events || []);
    } catch (e) {
      setEtaHistEvents([]);
    } finally {
      setEtaHistLoading(false);
    }
  };
  const onEtaHistToggle = (open) => {
    setEtaHistOpen(open);
    if (open) loadEtaHistory();
  };
  const initialPreview = pickOrderPreview(o);
  // Wave 3.99i — Graceful fallback: if the picked preview src actually
  // fails to decode (corrupt data URI, unsupported mime, etc.) we drop
  // to a placeholder that reflects the true attachment kind rather
  // than showing a broken-image glyph.
  const [imgFailed, setImgFailed] = React.useState(false);
  const preview = imgFailed
    ? { src: null, kind: (o.has_pdf ? 'pdf' : 'image_missing'), count: Number(o.image_count || 0) }
    : initialPreview;
  // Wave 3.99i — Prefer the server-side ``image_count`` hint (counts
  // ALL attachments including unrenderable PDFs / legacy orders that
  // never had ``images_thumb`` back-filled). Falls back to the local
  // array length so nothing regresses when the field is absent.
  const previewCount = Number(o.image_count || 0) || (Array.isArray(o.images) ? o.images.length : 0);
  const isTerminal = o.stage === 'Completed' || o.stage === 'Cancelled';
  // Wave 3.92 — Hover-preview attachments. Kept as state so the
  // popover can flip through multiple attachments without closing.
  const hoverAttachments = React.useMemo(() => collectHoverAttachments(o), [o]);
  const [hoverIdx, setHoverIdx] = React.useState(0);
  const activeHover = hoverAttachments[hoverIdx] || hoverAttachments[0] || null;

  return (
    <Card
      className="group relative flex flex-col overflow-hidden border border-[var(--divider)] hover:border-[var(--gold-accent)] hover:shadow-md transition-all duration-150"
      data-testid={`order-card-${o.id}`}
    >
      {/* Preview strip — wrapped in HoverCard so operators can see the
          full-res image + title in a popover without opening the order. */}
      <HoverCard openDelay={200} closeDelay={80}>
        <HoverCardTrigger asChild>
          <Link
            to={`/orders/${o.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block relative bg-gradient-to-br from-slate-50 to-slate-100 aspect-[4/3] overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--gold-accent)]"
            data-testid={`order-card-image-link-${o.id}`}
            title={`Open ${o.order_no}`}
          >
            {preview.kind === 'image' && preview.src ? (
              <img
                src={preview.src}
                alt={o.title || o.order_no}
                // Wave 3.98 — ``object-contain`` shows the full jewellery
                // photo scaled to fit the card without cropping (users
                // asked for a clear picture, not a zoomed-in crop). The
                // subtle background gradient fills any letterboxed
                // areas without dominating the card.
                className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-200"
                loading="lazy"
                onError={() => setImgFailed(true)}
                data-testid={`order-card-image-${o.id}`}
              />
            ) : (
              <div
                className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 text-slate-500"
                data-testid={`order-card-image-placeholder-${o.id}`}
              >
                {/* Wave 3.99i — Placeholders differentiate PDF-only
                    attachments from images whose thumbnail failed to
                    render (legacy orders). Both cases previously fell
                    to a generic "No image" state which hid the fact
                    that an attachment was actually uploaded. */}
                {preview.kind === 'pdf' ? (
                  <>
                    <FileText size={40} strokeWidth={1.2} className="text-rose-500" />
                    <div className="text-[11px] mt-1 uppercase tracking-wider font-medium">
                      PDF attached{previewCount > 1 ? ` (${previewCount})` : ''}
                    </div>
                  </>
                ) : preview.kind === 'image_missing' || preview.kind === 'unknown' ? (
                  <>
                    <ImageIcon size={40} strokeWidth={1.2} className="text-slate-400" />
                    <div className="text-[11px] mt-1 uppercase tracking-wider font-medium text-slate-500">
                      {previewCount === 1 ? '1 attachment' : `${previewCount} attachments`}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Open to view</div>
                  </>
                ) : (
                  <>
                    <FileText size={40} strokeWidth={1.2} className="text-slate-400" />
                    <div className="text-[11px] mt-1 uppercase tracking-wider font-medium">
                      No image
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Attachment count pill (top-right) */}
            {previewCount > 0 && (
              <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/60 text-white text-[10px] font-medium backdrop-blur-sm">
                <FileText size={10} />
                {previewCount}
              </div>
            )}
            {/* Priority pill — top-CENTER so it never collides with the
                selection checkbox (top-left) or the attachment count (top-right). */}
            {o.priority && o.priority !== 'normal' && (
              <div
                className={`absolute top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider shadow-sm ${
                  o.priority === 'urgent'
                    ? 'bg-rose-600 text-white'
                    : o.priority === 'high'
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-700 text-white'
                }`}
                title={`Priority: ${o.priority}`}
              >
                {o.priority}
              </div>
            )}
          </Link>
        </HoverCardTrigger>
        <HoverCardContent
          side="right"
          align="start"
          className="w-[360px] p-3 shadow-xl border-[var(--divider)]"
          data-testid={`order-card-hover-${o.id}`}
        >
          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <div
                className="text-[13px] font-semibold text-[var(--ink-navy)] leading-snug line-clamp-2"
                title={o.title}
                data-testid={`order-card-hover-title-${o.id}`}
              >
                {o.title || o.order_no}
              </div>
              <span className="font-mono text-[10px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-1.5 py-0.5 rounded shrink-0">
                {o.order_no}
              </span>
            </div>
            {/* Primary attachment view. For image → full-res render.
                For PDF with a rendered first page → show the thumb.
                For PDF without a thumb → show a friendly PDF placeholder. */}
            {activeHover ? (
              <div className="rounded-md overflow-hidden bg-slate-100 border border-[var(--divider)]">
                {activeHover.src ? (
                  <img
                    src={activeHover.src}
                    alt={activeHover.title || o.title || o.order_no}
                    className="w-full max-h-[280px] object-contain bg-slate-900/5"
                    data-testid={`order-card-hover-image-${o.id}`}
                  />
                ) : (
                  <div className="w-full h-[200px] flex flex-col items-center justify-center text-slate-500 bg-gradient-to-br from-slate-50 to-slate-100">
                    <FileText size={44} strokeWidth={1.2} />
                    <div className="text-[11px] mt-1 uppercase tracking-wider">PDF · preview unavailable</div>
                  </div>
                )}
                {(activeHover.title || activeHover.isPdf) && (
                  <div className="px-2 py-1 flex items-center justify-between gap-2 bg-white border-t border-[var(--divider)]">
                    <div className="text-[11px] text-slate-600 truncate" title={activeHover.title}>
                      {activeHover.title || (activeHover.isPdf ? 'PDF attachment' : 'Image attachment')}
                    </div>
                    {activeHover.isPdf && (
                      <Badge variant="outline" className="text-[9px] border-rose-200 bg-rose-50 text-rose-700 shrink-0">
                        PDF
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-[160px] rounded-md flex flex-col items-center justify-center text-slate-400 bg-gradient-to-br from-slate-50 to-slate-100 border border-[var(--divider)]">
                <FileText size={36} strokeWidth={1.2} />
                <div className="text-[11px] mt-1">No attachments</div>
              </div>
            )}
            {/* Attachment strip — click a thumb to swap the large preview.
                Only rendered when the order has more than one attachment. */}
            {hoverAttachments.length > 1 && (
              <div
                className="flex items-center gap-1.5 overflow-x-auto pb-1"
                data-testid={`order-card-hover-strip-${o.id}`}
              >
                {hoverAttachments.map((att, idx) => (
                  <button
                    type="button"
                    key={att.index}
                    onMouseEnter={() => setHoverIdx(idx)}
                    onFocus={() => setHoverIdx(idx)}
                    onClick={(e) => { e.preventDefault(); setHoverIdx(idx); }}
                    className={`shrink-0 relative w-12 h-12 rounded overflow-hidden border transition-all ${
                      idx === hoverIdx
                        ? 'border-[var(--gold-accent)] ring-1 ring-[var(--gold-accent)]'
                        : 'border-[var(--divider)] hover:border-slate-400'
                    }`}
                    aria-label={`Preview attachment ${idx + 1}`}
                    data-testid={`order-card-hover-thumb-${o.id}-${idx}`}
                  >
                    {att.src ? (
                      <img src={att.src} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400">
                        <FileText size={14} />
                      </div>
                    )}
                    {att.isPdf && (
                      <span className="absolute bottom-0 right-0 text-[7px] px-1 bg-rose-600 text-white font-bold">
                        PDF
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {/* Context row */}
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-slate-500 pt-1 border-t border-[var(--divider)]">
              {o.customer_name && <div className="truncate"><span className="text-slate-400">Customer · </span>{o.customer_name}</div>}
              <div className="truncate"><span className="text-slate-400">Goldsmith · </span>{o.goldsmith_name || '—'}</div>
              {o.jewellery_type && <div className="truncate"><span className="text-slate-400">Type · </span>{o.jewellery_type}</div>}
              {total > 0 && <div className="truncate font-mono tabular-nums"><span className="text-slate-400">Reqs · </span>{done}/{total}</div>}
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>

      {/* Body */}
      <div className="flex-1 flex flex-col p-3 gap-2">
        {/* Wave 4.x — External-CAD ribbon (left) + Completed ribbon (right).
            Both sit ABOVE the order number so they never cover the jewellery
            image, selection checkbox, urgency pill or attachment badge. The
            External-CAD tag bleeds to the left edge (notch tail on the right);
            the emerald COMPLETED tag bleeds to the right edge (notch tail on
            the left), running from centre to right. */}
        {(o.in_house_cad === false || o.stage === 'Completed') && (
          <div className="-mt-0.5 flex items-center justify-between gap-2 min-h-[20px]">
            {o.in_house_cad === false ? (
              <span
                className="inline-flex items-center gap-1 -ml-3 origin-left scale-90 bg-gradient-to-r from-pink-500 to-pink-400 text-white text-[9px] font-bold uppercase tracking-[0.12em] pl-3 pr-3.5 py-1 shadow-[1px_1px_2px_rgba(0,0,0,0.18)] [clip-path:polygon(0_0,100%_0,calc(100%-7px)_50%,100%_100%,0_100%)]"
                title="CAD developed externally"
                data-testid={`order-card-external-cad-${o.id}`}
              >
                <Cpu size={10} className="shrink-0" />
                External CAD
              </span>
            ) : <span />}
            {o.stage === 'Completed' && (
              <span
                className="inline-flex items-center gap-1 -mr-3 origin-right scale-90 bg-gradient-to-l from-emerald-600 to-emerald-500 text-white text-[9px] font-bold uppercase tracking-[0.12em] pl-3.5 pr-3 py-1 shadow-[-1px_1px_2px_rgba(0,0,0,0.18)] [clip-path:polygon(7px_0,100%_0,100%_100%,7px_100%,0_50%)]"
                title="Finished jewellery received — order completed"
                data-testid={`order-card-completed-${o.id}`}
              >
                <CheckCircle2 size={10} className="shrink-0" />
                Completed
              </span>
            )}
          </div>
        )}
        {/* Order No + Actions */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5 min-w-0">
            <Link
              to={`/orders/${o.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[12px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded hover:underline hover:bg-[var(--jewellery-bg)]/80 transition-colors self-start"
              data-testid={`order-card-no-link-${o.id}`}
            >
              {o.order_no}
            </Link>
            {o.reference_no != null && (
              <ReferenceChip
                referenceNo={o.reference_no}
                className="inline-flex items-center gap-0.5 font-mono text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded tabular-nums"
                title={`${refLabel} ${o.reference_no} — open in Order Forms (new tab)`}
                testId={`order-card-reference-${o.id}`}
              >
                <span className="text-[9px] uppercase tracking-wide opacity-70">{refLabelShort}</span>
                {o.reference_no}
              </ReferenceChip>
            )}
            {o.design_no && (
              <DesignNoBadge designNo={o.design_no} testId={`order-card-design-${o.id}`} />
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 -mr-1"
                data-testid={`order-card-actions-trigger-${o.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal size={14} />
                <span className="sr-only">Open order actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to={`/orders/${o.id}`} target="_blank" rel="noopener noreferrer" data-testid={`order-card-action-open-${o.id}`}>
                  <ExternalLink size={14} className="mr-2 text-[var(--ink-navy)]" />
                  Open Order
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {onOpenPdf && (
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); onOpenPdf?.(); }}
                data-testid={`order-card-action-print-${o.id}`}
                className="cursor-pointer text-blue-700 focus:text-blue-700"
              >
                <FileText size={14} className="mr-2" />
                Order Form PDF
              </DropdownMenuItem>
              )}
              {canToggleSla && !isTerminal && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); onOpenSla?.(); }}
                    data-testid={`order-card-action-sla-toggle-${o.id}`}
                    className="cursor-pointer"
                  >
                    <Bell size={14} className="mr-2 text-[var(--gold-accent)]" />
                    SLA Tracking…
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Title */}
        {o.title && (
          <div
            className="text-[13px] font-medium text-[var(--ink-navy)] leading-snug line-clamp-2"
            title={o.title}
            data-testid={`order-card-title-${o.id}`}
          >
            {o.title}
          </div>
        )}

        {/* Meta rows */}
        <div className="text-[11px] text-slate-600 space-y-1">
          {o.customer_name && (
            <div className="truncate" data-testid={`order-card-customer-${o.id}`}>
              <span className="text-slate-400">Customer · </span>
              <span className="font-medium text-[var(--ink-navy)]">{o.customer_name}</span>
            </div>
          )}
          <div className="truncate" data-testid={`order-card-goldsmith-${o.id}`}>
            <span className="text-slate-400">Goldsmith · </span>
            <span className="text-slate-700">{o.goldsmith_name || '—'}</span>
          </div>
          {/* Wave 3.99w — Always render the Jewellery Type row so
              buyers scanning the grid can see "Type · <name>" at the
              same eye-level on every card (previously the row hid
              when the field was empty, which made the grid look
              inconsistent). Empty value renders as a subtle em-dash. */}
          <div className="flex items-center gap-1.5" data-testid={`order-card-type-${o.id}`}>
            <span className="text-slate-400">Type · </span>
            {o.jewellery_type ? (
              <Badge
                variant="outline"
                className="bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] border-[var(--jewellery-icon)]/20 text-[10px] font-medium"
              >
                {o.jewellery_type}
              </Badge>
            ) : (
              <span className="text-slate-400">—</span>
            )}
          </div>
        </div>

        {/* Stage + SLA scope */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <StageBadge
            stage={o.stage}
            substage={o.substage}
            holdStatus={o.hold_status}
            activeSteps={o.active_steps}
          />
          <SlaScopeBadge scope={o.sla_scope} />
        </div>

        {/* Footer: reqs progress + created date (with age hint) and the
            Estimated Completion date directly beneath it (inline-editable). */}
        <div className="mt-auto pt-2 flex items-start justify-between gap-2 border-t border-[var(--divider)] text-[10px] text-slate-500">
          <span className="self-center" data-testid={`order-card-reqs-${o.id}`}>
            {total > 0 ? (
              <span className="font-mono tabular-nums">{done}/{total} reqs</span>
            ) : (
              <span className="text-slate-400">— reqs</span>
            )}
          </span>
          <div className="flex flex-col items-end gap-0.5">
            <span className="flex items-center gap-1.5 text-right">
              <span
                className="inline-flex items-center gap-1"
                data-testid={`order-card-created-${o.id}`}
                title={`Created ${formatDate(o.created_at)}`}
              >
                <CalendarRange size={11} className="text-slate-400 shrink-0" />
                <span className="font-mono tabular-nums">{formatDate(o.created_at)}</span>
              </span>
              {daysLabel && (
                <span className="text-slate-400" data-testid={`order-card-days-${o.id}`}>· {daysLabel}</span>
              )}
            </span>
            {/* Estimated Completion — shown below the created date. */}
            <span className="flex items-center gap-1" data-testid={`order-card-eta-${o.id}`}>
              <CalendarClock size={11} className="shrink-0 text-[var(--gold-accent)]" />
              <span className="text-slate-400">Est ·</span>
              {o.estimated_completion ? (
                <span className="font-mono tabular-nums text-slate-600" data-testid={`order-card-eta-value-${o.id}`}>{formatDate(o.estimated_completion)}</span>
              ) : (
                <span className="italic text-slate-400" data-testid={`order-card-eta-empty-${o.id}`}>Not set</span>
              )}
              {canEdit && (
                <Popover open={etaOpen} onOpenChange={setEtaOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="ml-0.5 inline-grid h-5 w-5 place-items-center rounded text-slate-400 transition-colors hover:bg-[var(--jewellery-bg)] hover:text-[var(--gold-accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--gold-accent)]"
                      aria-label="Edit estimated completion date"
                      data-testid={`order-card-eta-edit-${o.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Pencil size={11} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-[248px] p-3" data-testid={`order-card-eta-popover-${o.id}`}>
                    <div className="mb-1.5 text-[11px] font-semibold text-[var(--ink-navy)]">Estimated completion</div>
                    <DatePicker
                      value={etaDraft}
                      onChange={(e) => setEtaDraft(e.target.value)}
                      placeholder="DD-MM-YYYY"
                      data-testid={`order-card-eta-input-${o.id}`}
                    />
                    <div className="mt-2.5 flex items-center justify-between gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-[11px] text-slate-500 hover:text-rose-600"
                        disabled={etaSaving || !o.estimated_completion}
                        onClick={() => saveEta('')}
                        data-testid={`order-card-eta-clear-${o.id}`}
                      >
                        Clear
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 px-3 text-[11px] luxury-button"
                        disabled={etaSaving || (etaDraft || '') === (o.estimated_completion || '')}
                        onClick={() => saveEta(etaDraft)}
                        data-testid={`order-card-eta-save-${o.id}`}
                      >
                        {etaSaving ? 'Saving…' : 'Save'}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {/* Delivery-date change history — who changed it, when, old -> new. */}
              <Popover open={etaHistOpen} onOpenChange={onEtaHistToggle}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="ml-0.5 inline-grid h-5 w-5 place-items-center rounded text-slate-400 transition-colors hover:bg-[var(--jewellery-bg)] hover:text-[var(--gold-accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--gold-accent)]"
                    aria-label="View estimated delivery date history"
                    title="Delivery date change history"
                    data-testid={`order-card-eta-history-btn-${o.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <History size={11} />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[300px] p-0" data-testid={`order-card-eta-history-${o.id}`} onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1.5 border-b border-[var(--divider)] px-3 py-2 text-[11.5px] font-semibold text-[var(--ink-navy)]">
                    <History size={12} className="text-[var(--gold-accent)]" /> Delivery date history
                  </div>
                  <div className="max-h-64 overflow-auto p-2">
                    {etaHistLoading ? (
                      <div className="flex items-center justify-center gap-2 py-6 text-[12px] text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                    ) : !etaHistEvents || etaHistEvents.length === 0 ? (
                      <div className="py-6 text-center text-[12px] text-slate-400" data-testid={`order-card-eta-history-empty-${o.id}`}>No changes recorded yet.</div>
                    ) : (
                      <ul className="space-y-1.5" data-testid={`order-card-eta-history-list-${o.id}`}>
                        {etaHistEvents.map((ev) => (
                          <li key={ev.id} className="rounded-md border border-[var(--divider)] bg-[var(--paper-warm)]/40 px-2.5 py-1.5">
                            <div className="flex items-center gap-1.5 text-[11.5px] font-medium text-[var(--ink-navy)]">
                              <span className="font-mono tabular-nums text-slate-500">{ev.old ? formatDate(ev.old) : 'Not set'}</span>
                              <ArrowRight size={11} className="shrink-0 text-slate-400" />
                              <span className="font-mono tabular-nums text-[var(--gold-accent)]">{ev.new ? formatDate(ev.new) : 'Not set'}</span>
                            </div>
                            <div className="mt-0.5 flex items-center justify-between gap-2 text-[10.5px] text-slate-500">
                              <span className="truncate font-medium text-slate-600" title={ev.user_email}>{ev.user_name}</span>
                              <span className="shrink-0 tabular-nums">{formatDateTime(ev.timestamp)}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}


// ============================================================================
// Wave 3.25 — Inline "Add new Type of Jewellery" dialog. Used from the
// Order Form to extend the company's master list without leaving the
// dialog. Server-side ensures case-insensitive dedupe.
// ============================================================================
function JewelleryTypeAddDialog({ open, onOpenChange, companyId, onCreated }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) setName(''); }, [open]);

  const submit = async () => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      toast.error('Please enter a name');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        '/jewellery-types',
        { name: trimmed },
        { params: { company_id: companyId } },
      );
      toast.success(`Added "${data?.name || trimmed}"`);
      onOpenChange(false);
      if (onCreated) await onCreated(data);
    } catch (e) {
      toast.error(extractError(e, 'Failed to add type'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="jewellery-type-add-dialog">
        <DialogHeader>
          <DialogTitle>Add Jewellery Type</DialogTitle>
          <DialogDescription>
            Extend the master list (e.g. Bangle, Mangalsutra, Anklet). This will
            be available across all order forms in this company.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Type Name</Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Mangalsutra"
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              data-testid="jewellery-type-add-name-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !(name || '').trim()} className="luxury-button" data-testid="jewellery-type-add-submit-button">
            {saving ? 'Adding…' : 'Add Type'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Wave 3.85 — Compact SLA summary for the New Order form
// ----------------------------------------------------------------------------
// Replaces the previous inline ProductionSlaSection. Shows a single-line
// summary (SLA scope · priority · recipient counts) and a "Configure SLA"
// button that reuses the same EditOrderSlaDialog surfaced on Order Detail,
// but in draft mode (no order id yet — see EditOrderSlaDialog.jsx).
// SLA defaults are auto-seeded upstream via the /sla-template/resolve
// endpoint whenever the operator picks a Jewellery Type.
// ============================================================================
const PROCUREMENT_MATERIAL_LABELS = {
  gold: 'Gold',
  diamond: 'Diamond',
  color_stone: 'Colour Stone',
  pearl: 'Pearl',
};

function NewOrderSlaSummary({ form, template, slaCustomized, onOpenDialog }) {
  const scope = { timeline: true, procurement: true, ...(form.sla_scope || {}) };
  const scopeLabel = (!scope.timeline && !scope.procurement)
    ? { text: 'SLA Off', cls: 'text-rose-700 bg-rose-50 border-rose-200' }
    : (!scope.timeline || !scope.procurement)
      ? { text: 'SLA Partial', cls: 'text-amber-800 bg-amber-50 border-amber-200' }
      : { text: 'SLA On', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' };

  const stagesWithNotify = Object.values(form.delay_notify_by_stage || {}).filter((a) => (a?.length || 0) > 0).length;
  const procTotal = Object.values(form.procurement_config || {}).reduce((n, c) => n + ((c?.notify_user_ids || []).length), 0);
  const fallbackCount = (form.delay_notify_user_ids || []).length;

  // Show top-3 procurement SLA days as a summary chip
  const procRows = Object.entries(form.procurement_config || {}).map(([kind, cfg]) => ({
    kind,
    label: PROCUREMENT_MATERIAL_LABELS[kind] || kind,
    days: Number(cfg?.sla_days) || 0,
  }));

  return (
    <div className="border border-[var(--divider)] rounded-md p-3 bg-slate-50/50" data-testid="order-production-sla-summary">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[var(--ink-navy)]">Production SLA, Procurement &amp; Delay Alerts</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border ${scopeLabel.cls}`} data-testid="sla-summary-scope">
              {scopeLabel.text}
            </span>
            <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border border-[var(--divider)] bg-white text-slate-700 capitalize" data-testid="sla-summary-priority">
              Priority: {form.priority || 'normal'}
            </span>
            {slaCustomized ? (
              <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border border-[var(--gold-accent)] bg-[var(--paper-warm)] text-[var(--ink-navy)]" data-testid="sla-summary-customised">
                Customised
              </span>
            ) : (
              <span
                className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-500"
                title={form.jewellery_type
                  ? `Auto-filled from the SLA template for "${form.jewellery_type}"`
                  : 'Auto-filled from the base SLA template'}
                data-testid="sla-summary-auto"
              >
                Auto-filled{form.jewellery_type ? ` · ${form.jewellery_type}` : ''}
              </span>
            )}
          </div>
          <div className="mt-1.5 text-[11px] text-slate-500">
            {procRows.length > 0 && (
              <span className="mr-3">
                Procurement SLA:{' '}
                {procRows.map((r, i) => (
                  <span key={r.kind}>
                    <span className="font-mono tabular-nums">{r.days}d</span>{' '}
                    <span className="text-slate-500">{r.label}</span>
                    {i < procRows.length - 1 ? ' · ' : ''}
                  </span>
                ))}
              </span>
            )}
            <span>
              Recipients: {stagesWithNotify} stage{stagesWithNotify === 1 ? '' : 's'} · {procTotal} procurement · {fallbackCount} fallback
            </span>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onOpenDialog}
          data-testid="order-configure-sla-button"
        >
          Configure SLA
        </Button>
      </div>
    </div>
  );
}



// ============================================================================
// Wave 3.83 — SLA scope badge + toggle dialog
// ----------------------------------------------------------------------------
// Compact row indicator showing whether SLA tracking is fully On, Partial,
// or Off for the order. Rendered next to StageBadge in the Orders table.
// ============================================================================
function SlaScopeBadge({ scope }) {
  const s = scope || {};
  const timelineOff = s.timeline === false;
  const procurementOff = s.procurement === false;
  if (!timelineOff && !procurementOff) return null; // fully-on = no badge
  const bothOff = timelineOff && procurementOff;
  const label = bothOff ? 'SLA Off' : 'SLA Partial';
  const cls = bothOff
    ? 'bg-rose-50 text-rose-700 border-rose-200'
    : 'bg-amber-50 text-amber-800 border-amber-200';
  const title = bothOff
    ? 'SLA tracking is fully OFF for this order (no delay alerts)'
    : `SLA tracking is partial · timeline: ${timelineOff ? 'OFF' : 'ON'}, procurement: ${procurementOff ? 'OFF' : 'ON'}`;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${cls}`}
      title={title}
      data-testid="order-sla-scope-badge"
    >
      <BellOff size={10} />
      {label}
    </span>
  );
}

// ============================================================================
// Wave 3.83 — Per-order SLA tracking toggle dialog
// ----------------------------------------------------------------------------
// Simple dialog for CA/SA to enable/disable SLA tracking scopes:
//   - Production Timeline SLA
//   - Material Procurement SLA
// Optional reason is persisted onto the order's audit log via the
// backend endpoint POST /orders/{id}/sla-tracking.
// ============================================================================
function SlaTrackingToggleDialog({ open, order, onOpenChange, onSaved }) {
  const [scope, setScope] = useState({ timeline: true, procurement: true });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && order) {
      setScope({
        timeline: order?.sla_scope?.timeline !== false,
        procurement: order?.sla_scope?.procurement !== false,
      });
      setReason('');
    }
  }, [open, order]);

  if (!order) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/sla-tracking`, {
        scope,
        reason: reason.trim(),
      });
      toast.success('SLA tracking updated');
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update SLA tracking'));
    } finally {
      setSaving(false);
    }
  };

  const bothOff = !scope.timeline && !scope.procurement;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sla-tracking-toggle-dialog">
        <DialogHeader>
          <DialogTitle>SLA Tracking · {order.order_no}</DialogTitle>
          <DialogDescription>
            Turn SLA delay tracking on or off for this order. Tracks still update for visibility;
            only the delay alerts and counters are affected.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="flex items-start gap-3 rounded-md border border-[var(--divider)] px-3 py-2 cursor-pointer" data-testid="sla-toggle-timeline">
            <input
              type="checkbox"
              className="mt-1 accent-[var(--ink-navy)]"
              checked={!!scope.timeline}
              onChange={(e) => setScope((p) => ({ ...p, timeline: e.target.checked }))}
            />
            <div>
              <div className="text-[13px] font-medium text-[var(--ink-navy)]">Production Timeline SLA</div>
              <div className="text-[11px] text-slate-500">Delay alerts + counters for stages (CAD, CAM, Chamka, Ghat, Setting, QC, Delivery).</div>
            </div>
          </label>

          <label className="flex items-start gap-3 rounded-md border border-[var(--divider)] px-3 py-2 cursor-pointer" data-testid="sla-toggle-procurement">
            <input
              type="checkbox"
              className="mt-1 accent-[var(--ink-navy)]"
              checked={!!scope.procurement}
              onChange={(e) => setScope((p) => ({ ...p, procurement: e.target.checked }))}
            />
            <div>
              <div className="text-[13px] font-medium text-[var(--ink-navy)]">Material Procurement SLA</div>
              <div className="text-[11px] text-slate-500">Delay alerts per material track (Gold, Diamond, Colour Stone, Pearl).</div>
            </div>
          </label>

          <div>
            <Label className="text-[11px]">Reason (optional)</Label>
            <Textarea
              rows={2}
              placeholder="e.g. Rush job — SLA doesn't apply · Customer requested pause · Long-lead procurement"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="sla-toggle-reason"
            />
          </div>

          {bothOff && (
            <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              <BellOff size={12} className="inline-block mr-1" />
              SLA tracking will be fully OFF for this order. No delay alerts will fire.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="sla-toggle-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
